import React, { useEffect, useState, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import Froalaeditor from "froala-editor";
import FroalaEditor from "react-froala-wysiwyg";
import "froala-editor/js/plugins.pkgd.min.js";
import "froala-editor/css/froala_editor.pkgd.min.css";
import "froala-editor/css/froala_style.min.css";
import uuid from "react-uuid";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import MenuItem from "@mui/material/MenuItem";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import "./FroalaEditorStyles.scss";
import BulbIcon from "../../../assets/images/bulbIcon.png";
import EditOptionsDialog from "./EditOptionsDialog";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import toast from "react-hot-toast";
import CopySummaryModal from "../TemplateUtils/CopySummaryModal";
import { addGlobalList, editGlobalList, getGlobalListById } from "../../store/action";
import { useDispatch } from "react-redux";
import { CircularProgress } from "@mui/material";
import MenuBookIcon from '@mui/icons-material/MenuBook';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import { decodeEntities } from "../../utils/services";

const FroalaEditorComponent = (props) => {
  const {
    id,
    copy,
    type,
    showAI,
    editor,
    onChange,
    dialogRef,
    setCopied,
    editorData,
    showAIModal,
    commentData,
    froalaConfig,
    setShowAIModal,
    setEditorContent,
    setIsButtonAdded,
    handleDropdownList,
    onFocusEditor,
    commentOptionList,
    setCommentOptionList,
    setActiveEditorId,
    onBlur,
    handleChangeDropdown,
    AllCategoryList,
    filterTagList,
    newOpen
  } = props;

  const [state, setState] = useState({
    open: false,
    openHelp: false,
    editedOption: null,
    dialogOptionList: [],
    isdropdownAdded: true,
    selectedDropdown: null,
    openRemoveDropdown: false,
    dropdownList: id === "introTextTE" ? commentData?.introductory_text_dropdowns : id === "commentSummary" ? commentData?.summary_comment_dropdowns : commentData?.comment_dropdowns,
    selectedInput: null,
    summaryOptionList: [],
    isFocused: false,
    name: "", tags: [], description: "", saveToGlobalLists: false, listId: null, isDialogLoading: false
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const [currentInputId, setCurrentInputId] = useState(null);
  const [isDropdownLoading, setIsDropdownLoading] = useState(false);
  const dropdownOptionListRef = useRef([]);
  const savedRangeRef = useRef(null);
  const lastFocusedDropdownRef = useRef(null);
  const lastFocusedDynamicWrapperRef = useRef(null);
  const dynamicInputListenersRef = useRef(new Map());
  const dynamicInputObserverRef = useRef(null);
  const dynamicInputObserverRootRef = useRef(null);

  const detachDynamicInputListeners = useCallback((inputId) => {
    const entry = dynamicInputListenersRef.current.get(inputId);
    if (!entry) return;
    entry.inputEl.removeEventListener("change", entry.handleChange);
    entry.inputEl.removeEventListener("keydown", entry.handleKeydown);
    if (entry.handleFocus) entry.inputEl.removeEventListener("focus", entry.handleFocus);
    if (entry.handleBlur) entry.inputEl.removeEventListener("blur", entry.handleBlur);
    dynamicInputListenersRef.current.delete(inputId);
  }, []);

  const removeEmptyDynamicInputWrappers = useCallback((root) => {
    const editorRoot = root || editor.current?.editor?.$el?.get(0);
    if (!editorRoot) return false;
    const wrappers = editorRoot.querySelectorAll(".froala-dynamic-input-wrapper");
    let removed = false;
    wrappers.forEach((wrapper) => {
      if (wrapper.querySelector("input")) return;
      wrapper.remove();
      removed = true;
    });
    return removed;
  }, [editor]);

  const cleanupRemovedDynamicInputs = useCallback((mutations) => {
    const hasRemovals = mutations.some((m) => m.removedNodes.length > 0);
    if (!hasRemovals) return;
    if (dynamicInputListenersRef.current.size) {
      [...dynamicInputListenersRef.current.keys()].forEach((inputId) => {
        const entry = dynamicInputListenersRef.current.get(inputId);
        if (entry && !document.contains(entry.inputEl)) detachDynamicInputListeners(inputId);
      });
    }
    removeEmptyDynamicInputWrappers(dynamicInputObserverRootRef.current);
  }, [detachDynamicInputListeners, removeEmptyDynamicInputWrappers]);

  const ensureDynamicInputObserver = useCallback((editorRoot) => {
    if (!editorRoot) return;
    if (dynamicInputObserverRef.current && dynamicInputObserverRootRef.current === editorRoot) return;
    dynamicInputObserverRef.current?.disconnect();
    dynamicInputObserverRef.current = new MutationObserver(cleanupRemovedDynamicInputs);
    dynamicInputObserverRef.current.observe(editorRoot, { childList: true, subtree: true });
    dynamicInputObserverRootRef.current = editorRoot;
  }, [cleanupRemovedDynamicInputs]);

  const registerDynamicInputListeners = useCallback((inputEl, inputId, handleKeydown, handleChange) => {
    detachDynamicInputListeners(inputId);
    const handleFocus = () => {
      lastFocusedDynamicWrapperRef.current = inputEl.closest(".froala-dynamic-input-wrapper");
    };
    const handleBlur = (event) => {
      const relId = event.relatedTarget?.id;
      if (relId !== "add-input" && relId !== "add-dropdown") {
        // Keep reference active so selection can be restored after input wrapper
      }
    };
    dynamicInputListenersRef.current.set(inputId, { inputEl, handleChange, handleKeydown, handleFocus, handleBlur });
    inputEl.addEventListener("change", handleChange);
    inputEl.addEventListener("keydown", handleKeydown, true);
    inputEl.addEventListener("focus", handleFocus);
    inputEl.addEventListener("blur", handleBlur);
  }, [detachDynamicInputListeners]);

  const placeCaretInEditorAt = useCallback((anchorNode, parentNode) => {
    const activeEditor = editor.current?.editor;
    if (!activeEditor) return;
    try { activeEditor.events.focus(); } catch (err) {}
    const sel = window.getSelection();
    if (!sel) return;
    const newRange = document.createRange();
    if (anchorNode && document.contains(anchorNode)) {
      if (anchorNode.nodeType === Node.TEXT_NODE) {
        newRange.setStart(anchorNode, anchorNode.textContent.length);
      } else {
        newRange.setStartAfter(anchorNode);
      }
    } else if (parentNode && document.contains(parentNode)) {
      newRange.setStart(parentNode, 0);
    } else {
      return;
    }
    newRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(newRange);
    savedRangeRef.current = newRange.cloneRange();
  }, [editor]);

  const normalizeDynamicInputSeparators = useCallback((editorRoot) => {
    if (!editorRoot) return;
    const wrappers = editorRoot.querySelectorAll(".froala-dynamic-input-wrapper");
    wrappers.forEach((wrapper) => {
      const whitespaceNodes = [];
      let next = wrapper.nextSibling;
      while (next && next.nodeType === Node.TEXT_NODE && /^[\s\u00A0]*$/.test(next.textContent)) {
        whitespaceNodes.push(next);
        next = next.nextSibling;
      }
      if (whitespaceNodes.length > 0) {
        whitespaceNodes.slice(1).forEach((node) => node.remove());
        whitespaceNodes[0].textContent = "\u00A0";
      }
    });
  }, []);

  const getListItemFromRange = (range) => {
    if (!range) return null;
    let node = range.startContainer;
    if (node?.nodeType === Node.TEXT_NODE) node = node.parentElement;
    return node?.closest?.("li") || null;
  };

  const isListItemEffectivelyEmpty = (listItem) =>
    !listItem?.innerHTML?.replace(/&nbsp;|<br\s*\/?>/gi, "").trim();

  const listItemFromListElement = (listEl) => {
    if (!listEl?.matches?.("ol, ul")) return null;
    const items = listEl.querySelectorAll(":scope > li");
    if (!items.length) return null;
    const emptyLi = [...items].reverse().find(isListItemEffectivelyEmpty);
    return emptyLi || items[items.length - 1];
  };

  // Froala often leaves the caret as a sibling of <ol>/<ul> (not inside <li>) on empty lists.
  const resolveTargetListItem = (editorRoot, range, editorInstance) => {
    const fromRange = getListItemFromRange(range);
    if (fromRange && editorRoot?.contains(fromRange)) return fromRange;

    try {
      const selEl = editorInstance?.selection?.element?.();
      const fromSelection = selEl?.closest?.("li");
      if (fromSelection && editorRoot?.contains(fromSelection)) return fromSelection;
    } catch (_) {}

    if (!range || !editorRoot) return null;

    const { startContainer, startOffset } = range;
    const tryAdjacentList = (node) => listItemFromListElement(node);

    if (startContainer === editorRoot || startContainer?.classList?.contains?.("fr-element")) {
      return (
        tryAdjacentList(startContainer.childNodes[startOffset - 1]) ||
        tryAdjacentList(startContainer.childNodes[startOffset])
      );
    }

    if (startContainer.nodeType === Node.ELEMENT_NODE) {
      const fromChild = tryAdjacentList(startContainer.childNodes[startOffset - 1]);
      if (fromChild) return fromChild;
    }

    if (startContainer.nodeType === Node.TEXT_NODE) {
      let sib = startContainer.previousSibling;
      while (sib) {
        const li = tryAdjacentList(sib);
        if (li) return li;
        if (sib.nodeType === Node.ELEMENT_NODE && !sib.matches?.("ol, ul")) break;
        sib = sib.previousSibling;
      }
    }

    return null;
  };

  const buildDynamicInputWrapperHtml = (inputId) => {
    const inputStyle = "width:104px;background-color:#f1f9fe;height:24px !important;overflow-wrap:break-word;padding:1px 17px 3px 6px;border-radius:4px;border:1px solid #0F75BC;appearance:none;-webkit-appearance:none;-moz-appearance:none;font-size:14px;color:#3B3B3B;font-style:normal;font-weight:600;max-width:200px;min-width:104px;text-overflow:ellipsis;white-space:nowrap;";
    return `<span contenteditable="false" class="froala-dynamic-input-wrapper"><input id="${inputId}" type="text" value="" title="" style="${inputStyle}"/></span>`;
  };

  const buildDropdownWrapperHtml = (selectUuid, editorNameId) =>
    `<span contenteditable="false" id="dropdown_${selectUuid}" class="froala-dropdown-wrapper" style="display: inline-flex; align-items: center; vertical-align: middle; line-height: normal;">
         <input
            type="text"
            class="froala-dropdown-input"
            id="${selectUuid}"
            name="${editorNameId}"
            value=""
            placeholder="Type or select from dropdown"
            style="
              width: 220px;
              background-color: #f1f9fe;
              height: 23px;
              padding: 4px 8px;
              vertical-align: middle;
              border-top: 1px solid #4282e2;
              border-bottom: 1px solid #4282e2;
              border-left: 1px solid #4282e2;
              border-right: none;
              border-radius: 4px 0 0 4px;
              font-size: 14px;
              font-weight: 600;
              color: #3B3B3B;
              cursor: text;
              text-align: left;
            "
            autocomplete="off"
            autocorrect="off"
            spellcheck="false"
          />
          <button
            type="button"
            id="dropdownIcon_${selectUuid}"
            name="${editorNameId}"
            data-dropdown-id="${selectUuid}"
            onclick="window.openDropdown('${selectUuid}', event)"
            style="
              background-color: #f1f9fe;
              width: 26px;
              height: 23px;
              border: 1px solid #4282e2;
              border-right: 0;
              margin-left: -4px;
              font-size: 14px;
              color: #4282e2;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              padding-right: 3px;
              cursor: pointer;
            "
          >▾</button>
          <button
            id="dropdownButton${selectUuid}"
            name="${editorNameId}"
            style="
              background-color: #f1f9fe;
              width: 24px;
              height: 23px;
              border: 1px solid #4282e2;
              border-radius: 0 4px 4px 0;
              margin-left: -4px;
              font-size: 14px;
              color: #4282e2;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
            "
          >✎</button>
        </span>&nbsp;`;

  const insertInlineWrapperInListItem = (listItem, wrapperEl, insertRange, editorInstance) => {
    const spaceNode = document.createTextNode("\u00A0");

    if (isListItemEffectivelyEmpty(listItem)) {
      listItem.innerHTML = "";
    }

    const lastWrapperInLi = listItem.querySelector(
      ".froala-dynamic-input-wrapper:last-of-type, .froala-dropdown-wrapper:last-of-type"
    );

    if (
      insertRange &&
      listItem.contains(insertRange.startContainer) &&
      document.contains(insertRange.startContainer)
    ) {
      insertRange.collapse(true);
      insertRange.insertNode(wrapperEl);
      wrapperEl.after(spaceNode);
    } else if (lastWrapperInLi) {
      const afterWrapperRange = document.createRange();
      const nextSib = lastWrapperInLi.nextSibling;
      if (nextSib?.nodeType === Node.TEXT_NODE) {
        afterWrapperRange.setStart(nextSib, nextSib.textContent.length);
      } else {
        afterWrapperRange.setStartAfter(lastWrapperInLi);
      }
      afterWrapperRange.collapse(true);
      afterWrapperRange.insertNode(wrapperEl);
      wrapperEl.after(spaceNode);
    } else {
      listItem.appendChild(wrapperEl);
      listItem.appendChild(spaceNode);
    }
    editorInstance.events.trigger("contentChanged");
    placeCaretInEditorAt(spaceNode, listItem);
    return wrapperEl;
  };

  const insertDynamicInputInListItem = (listItem, inputId, insertRange, editorInstance) => {
    const temp = document.createElement("div");
    temp.innerHTML = buildDynamicInputWrapperHtml(inputId);
    return insertInlineWrapperInListItem(listItem, temp.firstElementChild, insertRange, editorInstance);
  };

  const finalizeWrapperPlacement = (elementId, wrapperSelector, editorInstance, editorRoot, targetListItem) => {
    const newlyInserted = editorRoot.querySelector(`#${CSS.escape(elementId)}`);
    if (!newlyInserted) return null;

    const newWrapper = newlyInserted.closest(wrapperSelector);
    if (!newWrapper) return null;

    if (!newWrapper.closest("li")) {
      if (relocateWrapperIntoAdjacentList(newWrapper, editorRoot)) {
        editorInstance.events.trigger("contentChanged");
      }
    }

    const parentEl = newWrapper.parentElement;
    if (parentEl?.tagName === "P") {
      const liParent = parentEl.closest("li");
      if (liParent) {
        liParent.insertBefore(newWrapper, parentEl);
        liParent.insertBefore(document.createTextNode("\u00A0"), parentEl);
        const pContent = parentEl.innerHTML.replace(/&nbsp;|<br\s*\/?>/gi, "").trim();
        if (!pContent) parentEl.remove();
      } else if (targetListItem && editorRoot.contains(targetListItem) && !targetListItem.contains(newWrapper)) {
        if (isListItemEffectivelyEmpty(targetListItem)) targetListItem.innerHTML = "";
        targetListItem.appendChild(newWrapper);
        targetListItem.appendChild(document.createTextNode("\u00A0"));
        const orphanP = newWrapper.parentElement?.tagName === "P" ? newWrapper.parentElement : null;
        if (orphanP) {
          const orphanContent = orphanP.innerHTML.replace(/&nbsp;|<br\s*\/?>/gi, "").trim();
          if (!orphanContent) orphanP.remove();
        }
      }
    } else if (!newWrapper.closest("li")) {
      if (relocateWrapperIntoAdjacentList(newWrapper, editorRoot)) {
        editorInstance.events.trigger("contentChanged");
      }
    }

    return newWrapper;
  };

  const relocateWrapperIntoAdjacentList = (wrapper, editorRoot) => {
    if (!wrapper || !editorRoot || wrapper.closest("li")) return false;

    let listEl = null;
    let sib = wrapper.previousSibling;
    while (sib) {
      if (sib.nodeType === Node.ELEMENT_NODE && (sib.matches("ol") || sib.matches("ul"))) {
        listEl = sib;
        break;
      }
      sib = sib.previousSibling;
    }

    const listItem = listItemFromListElement(listEl);
    if (!listItem) return false;

    if (isListItemEffectivelyEmpty(listItem)) {
      listItem.innerHTML = "";
    }

    listItem.appendChild(wrapper);
    const next = wrapper.nextSibling;
    if (!(next?.nodeType === Node.TEXT_NODE && /^[\s\u00A0]*$/.test(next.textContent))) {
      wrapper.after(document.createTextNode("\u00A0"));
    }
    return true;
  };

  useEffect(() => {
    return () => {
      dynamicInputObserverRef.current?.disconnect();
      dynamicInputObserverRef.current = null;
      dynamicInputObserverRootRef.current = null;
      [...dynamicInputListenersRef.current.keys()].forEach(detachDynamicInputListeners);
    };
  }, [detachDynamicInputListeners]);

  const storeData = useSelector((state) => state.commentLibrary);
  const location = useLocation();
  const dispatch = useDispatch();
  const { openHelp, dropdownList, selectedDropdown, dialogOptionList, openRemoveDropdown, summaryOptionList, isFocused, open, name, tags, description, saveToGlobalLists, listId, listGuid } = state;
  const templateData = useSelector((state) => state.templates).templateData;
  const isNarrativeTemplate = templateData?.template?.is_narrative_template;

  const categoryTags = React.useMemo(() => {
    const categories = (AllCategoryList || templateData)?.template?.categories || [];
    const selectedCategoryFromStore = storeData?.showClModal?.selectedCategory;

    let currentCategory = categories.find(cat => cat.category_id === selectedCategoryFromStore?.id);

    if (!currentCategory && commentData?.comment_id) {
      currentCategory = categories.find(cat =>
        cat.lines?.some(line =>
          line.comments?.some(com => com.comment_id === commentData?.comment_id || com.comment_uuid === commentData?.comment_uuid)
        )
      );
    }

    const categoryTags = currentCategory?.narrative_categories_tags || currentCategory?.nn_categories_tags || [];
    return categoryTags.map(item => ({
      id: item.tag?.id,
      title: item.tag?.name || item.tag?.title,
      color: item.tag?.color
    })).filter(tag => tag.id);
  }, [AllCategoryList, templateData, storeData?.showClModal?.selectedCategory, commentData?.comment_id, commentData?.comment_uuid]);

  const handleOpenDialog = async (event, dropdownsList) => {
    event?.stopPropagation();
    setState({ ...state, open: true, isDialogLoading: true });
    let response
    if ((event?.target?.name === "commentText" || event?.target?.name === "nonNarrativeComment") && setActiveEditorId) {
      setActiveEditorId(event?.target?.name);
    }
    else if (event?.target?.name === "commentSummary" && setActiveEditorId) {
      setActiveEditorId(event?.target?.name);
    }
    let newButtonId = event?.target?.id?.replace("dropdownButton", "");
    if (event?.target?.tagName === "BUTTON") {
      let dropdown;
      dropdown = dropdownList?.find((obj) => obj?.dropdown_uuid === newButtonId);
      if (dropdown === undefined) dropdown = editorData?.dropdownList?.find((obj) => obj?.isPasted === true);
      if (dropdown?.length === 0 || dropdown === undefined) dropdown = dropdownsList?.find((obj) => obj?.dropdown_uuid === newButtonId);
      if (dropdown === undefined) { if (id === "commentText" || id === "nonNarrativeComment") dropdown = commentData?.comment_dropdowns?.find((obj) => obj?.dropdown_uuid === newButtonId); else dropdown = commentData?.summary_comment_dropdowns?.find((obj) => obj?.dropdown_uuid === newButtonId); }
      if (dropdown === undefined) dropdown = editorData?.dropdownList?.find((obj) => obj?.dropdown_uuid === newButtonId);
      if (dropdown?.list_guid) {
        response = await dispatch(getGlobalListById(dropdown?.list_guid));
        if (response?.deleted_at) {
          const editorInstance = editor?.current?.editor;
          let editorContent = editorInstance?.html?.get();
          let newDropdownList = editorData?.dropdownList || dropdownsList || ((id === "commentText" || id === "nonNarrativeComment") ? commentData?.comment_dropdowns : commentData?.summary_comment_dropdowns);
          let newIndex = newDropdownList?.findIndex((obj => obj?.list_guid === dropdown?.list_guid))
          newDropdownList[newIndex] = { ...newDropdownList[newIndex], list_guid: undefined, dropdowns_values: response?.options };
          if (id === "introTextTE" && setEditorContent) {
            onBlur?.(id, editorContent, newDropdownList[newIndex], "edit");
            handleDropdownList?.(id, newDropdownList);
            setEditorContent(prevState => ({ ...prevState, dropdownList: newDropdownList }));
          } else if (id === "commentText" && setEditorContent) {
            onBlur?.(id, editorContent, newDropdownList);
            setEditorContent(prevState => ({ ...prevState, commentText: { dropdownList: newDropdownList }, commentDropdownList: newDropdownList }));
          } else if (id === "commentSummary" && setEditorContent) {
            onBlur?.(id, editorContent, newDropdownList);
            setEditorContent(prevState => ({ ...prevState, commentSummary: { dropdownList: newDropdownList }, summaryDropdownList: newDropdownList }));
          } else if (id === "nonNarrativeComment") {
            onBlur?.(id, editorContent, newDropdownList);
            setEditorContent(prevState => ({ ...prevState, dropdownList: newDropdownList }));
          }
        }
      }
      setAnchorEl(null);
      setState((prevState) => ({
        ...prevState,
        dialogOptionList: response?.options || dropdown?.dropdowns_values,
        open: "edit_list",
        selectedDropdown: newButtonId || dropdown?.dropdown_uuid,
        name: response?.name,
        description: response?.description,
        tags: response?.tags,
        saveToGlobalLists: dropdown?.list_guid ? response?.deleted_at ? false : true : false,
        listId: response?.deleted_at ? undefined : response?.id,
        listGuid: response?.deleted_at ? undefined : dropdown?.list_guid,
      }));
      setState((prevState) => ({ ...prevState, isDialogLoading: false }))
    }
  };

  useEffect(() => {
    const editorInstance = editor?.current?.editor;
    let editorContent = editorInstance?.html?.get();
    if (!editorInstance || !editorData?.innerHTML) return;
    let isEffectExecuted = false;
    let newDropdownList = dropdownList?.length > 0 ? [...dropdownList] : [];
    const returnButtonElement = (dropdown) => {
      const buttonElement = document.createElement("button");
      buttonElement.setAttribute("id", `dropdownButton${dropdown.dropdown_uuid}`);
      buttonElement.setAttribute("contenteditable", "false");
      buttonElement.setAttribute("name", `${dropdown.dropdown_uuid}`);
      buttonElement.classList.add("dropdown-button");
      buttonElement.setAttribute("type", "button");
      buttonElement.addEventListener("click", (event) => {
        handleOpenDialog(event, dropdown.dropdown_uuid);
      });
      buttonElement.style.cssText = `background-color: #f1f9fe;
          width: 24px;
          height: 24px;
          padding: 0px;
          color: #0F75BC;
          border-radius: 4px 0px 0px 4px;
          border: 1px solid #0F75BC;
          position: relative;
          transform: scaleX(-1);
          font-size: 17px;
          margin-left: -4px;
          top: 2.5px;`;
      buttonElement.textContent = "✎";
      return buttonElement;
    }
    const returnStyleElement = (dropdown) => {
      const styleTag = document.createElement("style");
      styleTag.innerHTML = `
        @media screen and (max-width: 1100px) {
          #dropdownButton${dropdown.dropdown_uuid} {
            top: 25.5px;
          }
        }
        @media screen and (min-width: 1600px) {
          #dropdownButton${dropdown.dropdown_uuid} {
            top: 2.5px;
            width: initial;
            margin-left: -5px;
          }
        }
        @media screen and (min-width: 1700px) {
          #dropdownButton${dropdown.dropdown_uuid} {
            top: 2.5px;
            width: initial;
            margin-left: -5px;
          }
        }
        @media screen and (min-width: 1800px) {
          #dropdownButton${dropdown.dropdown_uuid} {
            top: 2.5px;
            width: initial;
            margin-left: -5px;
          }
        }
        @media screen and (min-width: 1900px) {
          #dropdownButton${dropdown.dropdown_uuid} {
            top: 2px;
            width: initial;
            margin-left: -5px;
          }
        }
        @media screen and (min-width: 2000px) {
          #dropdownButton${dropdown.dropdown_uuid} {
            top: 1.5px;
            width: initial;
            margin-left: -5px;
          }
        }
        @media screen and (min-width: 2100px) {
          #dropdownButton${dropdown.dropdown_uuid} {
            top: 1.5px;
            width: initial;
            margin-left: -5px;
          }
        }
      `;
      return styleTag;
    }
    const replacePlaceholdersWithDropdowns = () => {
      const dropdowns = id === "introTextTE" ? commentData?.introductory_text_dropdowns : id === "commentText" || id === "nonNarrativeComment" ? commentData?.comment_dropdowns : commentData?.summary_comment_dropdowns;
      const regexPlainDropdown = /\[dropdown ([a-f0-9\-]+)\]/gi;
      if (Array?.isArray(dropdowns) && dropdowns?.length > 0) {
        const tempContainer = document.createElement("div");
        tempContainer.innerHTML = editorData?.innerHTML;
        if (copy) {
          dropdowns.forEach((dropdown) => {
            const spanElements = Array.from(tempContainer.querySelectorAll(`span[contenteditable="false"][id="[dropdown ${dropdown.dropdown_uuid}]"]`));
            spanElements.forEach((spanElement) => {
              let optionValues = [];
              spanElement.innerHTML = "";
              spanElement.contentEditable = "false";
              const selectElement = document.createElement("select");
              let newDropdownValues = Array.isArray(dropdown?.dropdowns_values?.values) ? dropdown?.dropdowns_values?.values : dropdown?.dropdowns_values;
              if (Array.isArray(newDropdownValues)) {
                newDropdownValues = newDropdownValues.filter((obj) => !obj.toUpperCase().includes("XXXXX"));
                if (!newDropdownValues.find((value) => value.includes("------"))) {
                  newDropdownValues.unshift("-------------");
                }
                newDropdownValues.forEach((value) => {
                  const option = document.createElement("option");
                  option.text = value?.length > 40 ? `${value.substring(0, 40)}...` : value || "";
                  option.value = value || "";
                  option.title = value || "";
                  if (dropdown?.selected_value === value) {
                    option.setAttribute("selected", value);
                  }
                  selectElement.appendChild(option);
                });
              }
              selectElement.classList.add(`${id === "introTextTE" ? "intro" : id === "commentText" ? "comment" : "summary"}-dropdown`);
              selectElement.setAttribute("id", dropdown.dropdown_uuid);
              if (id !== "introTextTE") {
                selectElement.setAttribute("comment_id", commentData?.comment_id);
                selectElement.setAttribute("comment_uuid", commentData?.comment_uuid);
                selectElement.setAttribute("dropType", id === "commentText" ? "comment" : "summary");
              }
              optionValues = Array.from(selectElement.options)
                .map((option) => option.value)
                .filter((value) => value !== "-------------");
              newDropdownList.push({
                dropdown_uuid: dropdown?.dropdown_uuid,
                name: dropdown?.name || "",
                selected_value: dropdown?.selected_value,
                dropdowns_values: optionValues || [],
                isPasted: true,
              });
              spanElement.appendChild(selectElement);
              const buttonElement = returnButtonElement(dropdown);
              spanElement.appendChild(buttonElement);
              const styleTag = returnStyleElement(dropdown);
              document.head.appendChild(styleTag);
            });
          });
          editorData.innerHTML = tempContainer.innerHTML;
        } else {
          var placeholderRegex = /\[dropdown\s([a-f0-9-]+)\]/gi;
          const selectRegex = /<select\b[^>]*id="([a-f0-9-]+)"[^>]*>([\s\S]*?)<\/select>/gi;
          editorData.innerHTML = editorData?.innerHTML
            ?.replace(placeholderRegex, (match, dropdownId) => createDropdownHTML(dropdownId))
            ?.replace(selectRegex, (match, dropdownId) => createDropdownHTML(dropdownId));
          function createDropdownHTML(dropdownId) {
            const dropdown = dropdowns?.find((item) => item?.dropdown_uuid === dropdownId);
            if (!dropdown) return "";
            newDropdownList.push({
              dropdown_uuid: dropdownId,
              name: dropdown?.name || "",
              selected_value: dropdown?.selected_value,
              dropdowns_values: dropdown?.dropdowns_values || [],
              isPasted: true,
            });
            const safeValue = dropdown?.selected_value?.trim().toUpperCase() === "XXX" ? "" :
              (dropdown?.selected_value || "").replace(/\\/g, "\\\\").replace(/"/g, "&quot;");
            const commentAttrs = id !== "introTextTE"
              ? ` comment_id="${commentData?.comment_id}" comment_uuid="${commentData?.comment_uuid}" dropType="${id === "commentText" || id === "nonNarrativeComment" ? "comment" : "summary"}"`
              : "";
            return ` <span contenteditable="false" style="display:inline-flex; align-items:center; vertical-align:middle; line-height:normal; margin: 0 2px;" id="[dropdown ${dropdownId}]">
                          <input id="${dropdownId}" ${commentAttrs}  class="froala-dropdown-input"
                              value="${safeValue}"
                              placeholder="Type or select from dropdown"
                              autocomplete="off"
                              autocorrect="off"
                              spellcheck="false"
                              style="width: 220px;
                              background-color: #f1f9fe;
                              height: 23px; 
                              padding: 4px 8px;
                              vertical-align: middle;
                              border-top: 1px solid #4282e2;
                              border-bottom: 1px solid #4282e2;
                              border-left: 1px solid #4282e2;
                              border-right: none; 
                              border-radius: 4px 0px 0px 4px; 
                              font-size: 14px;
                              font-weight: 600;
                              color: #3B3B3B;
                              cursor: text;
                              text-align: left;
                              display: inline-block;"
                              />
                               <style>
                              .froala-dropdown-input::placeholder {
                                font-style: italic;
                                font-weight:400;
                                }
                              </style>
                              <button
                                contenteditable="false" 
                                class="fr-disabled"
                                tabindex="-1"
                                id="dropdownIcon_${dropdownId}"
                                name="${id}"
                                type="button"
                                data-dropdown-id="${dropdownId}"
                                onclick="window.openDropdown('${dropdownId}', event)"
                                style="background-color: #f1f9fe;
                                width: 23px;
                                height: 23px !important;
                                border: 1px solid #4282e2;
                                border-right: 0px solid blue;
                                vertical-align: middle;
                                margin-left: 0px;
                                font-size: 14px;
                                color: #4282e2;
                                display: inline-flex;
                                align-items: center;
                                justify-content: center;
                                cursor: pointer;"
                              >
                              ▾
                              </button>
                              <button id="dropdownButton${dropdownId}" name="${id}"
                              class="fr-disabled"
                              contenteditable="false" 
                              tabindex="-1"
                              style="background-color: #f1f9fe;
                              width: 24px; 
                              height: 23px !important; 
                              border: 1px solid #4282e2;
                              border-radius: 0px 4px 4px 0px;
                              margin-left:0px;
                              font-size: 14px;
                              color: #4282e2;
                              display: inline-flex;
                              align-items: center;
                              justify-content: center;
                              vertical-align: middle;
                              cursor: pointer;">
                                ✎
                              </button>
                    </span>`;
          }
        }
      } else {
        const outputString = editorData?.innerHTML?.replace(regexPlainDropdown, (match, selectId) => {
          return ` <span id="[dropdown ${selectId}]" contenteditable="false" style="display: inline-flex; align-items: center; margin-left:2px;"><input id="${selectId}" value=""
                              placeholder="Type or select from dropdown"
                              autocomplete="off"
                              autocorrect="off"
                              spellcheck="false"
                              style="width: 220px;
                              background-color: #f1f9fe;
                              height: 23px !important; 
                              padding: 4px 8px;
                              vertical-align: middle;
                              border-top: 1px solid #4282e2;
                              border-bottom: 1px solid #4282e2;
                              border-left: 1px solid #4282e2;
                              border-right: none; 
                              border-radius: 4px 0px 0px 4px; 
                              font-size: 14px;
                              font-weight: 600;
                              color: #3B3B3B;
                              cursor: text;
                              text-align: left;
                              display: inline-block;"
                              class="froala-dropdown-input"
                              />
                               <style>
                              .froala-dropdown-input::placeholder {
                                font-style: italic ! important;
                                font-weight:400 ! important;
                                }
                              </style><button type="button" contenteditable="false"
                                name="${id}"
                                id="dropdownIcon_${selectId}"
                                data-dropdown-id="${selectId}"
                                onclick="window.openDropdown('${selectId}', event, null, '${commentData?.comment_uuid}')"
                                style="background-color: #f1f9fe;
                                  width: 26px;
                                  height: 24px !important;
                                  border: 1px solid #4282e2;
                                  border-right: 1px solid #4282e2;
                                  vertical-align: middle;
                                  margin-left: -3px;
                                  font-size: 14px;
                                  color: #4282e2;
                                  display: inline-flex;
                                  align-items: center;
                                  justify-content: center;
                                  cursor: pointer;
                                  padding-right: 2px;
                                  border-radius: 0px 4px 4px 0px;">
                                ▾
                              </button><button contenteditable="false"
                            id="dropdownButton${selectId}"
                            name="${id}"
                            style="
                              background-color: #f1f9fe;
                              width: 24px;
                              height: 24px;
                              border: 1px solid #4282e2;
                              border-radius: 0 4px 4px 0;
                              margin-left: -3px;
                              font-size: 14px;
                              color: #4282e2;
                              display: inline-flex;
                              align-items: center;
                              justify-content: center;
                              cursor: pointer;
                            "
                          >✎</button></span>`
        });
        editorData.innerHTML = outputString;
      }
      return copy ? decodeEntities(editorData.innerHTML) : editorData.innerHTML;
    };
    const replaceInputs = (input_text) => {
      let container = document.createElement("div");
      container.innerHTML = input_text;
      container.innerHTML = container.innerHTML.replace(/\[textbox (.*?)\]/g, (match, attributes) => {
        let idMatch = attributes.match(/id:([^;]+);?/);
        let valueMatch = attributes.match(/value:([^;]+);?/);
        let inputId = idMatch && idMatch[1].length > 30 ? idMatch[1].trim() : uuid();
        let value = valueMatch ? valueMatch[1].trim() : "";
        const safeValue = value.replace(/\\/g, "\\\\").replace(/"/g, "&quot;");
        return `<input id="${inputId}" value="${safeValue}" title="${safeValue}" style="width: 104px;
                background-color: #f1f9fe;
                height: 24px !important;
                overflow-wrap: break-word;
                padding: 1px 17px 3px 6px;
                border-radius: 4px;
                border: 1px solid #0F75BC;
                appearance: none;
                -webkit-appearance: none;
                -moz-appearance: none;
                font-size: 14px !important;
                color: #3B3B3B !important;
                font-style: normal !important;
                font-weight: 600 !important;
                max-width: 200px !important;
                min-width: 104px !important;
                text-overflow: ellipsis !important;
                white-space: nowrap !important;"
                onblur="handleChangeInput(inputId, value)"
                />`;
      });
      return container.innerHTML;
    }
    setTimeout(() => {
      if (!isEffectExecuted && (editorContent === undefined || copy)) {
        isEffectExecuted = true;
        let newHtmlData = replacePlaceholdersWithDropdowns();
        newHtmlData = replaceInputs(newHtmlData);
        editorInstance.html.set(newHtmlData);
      }
    }, 100);
    return () => {
      isEffectExecuted = false;
    };
  }, [commentData, id, copy]);

  const handleModelChange = (model) => {
    if (setCopied) setCopied(false);
    let htmlString = model;
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");
    const pastedElements = doc?.querySelectorAll(`[id^="isPasted"], [id^="pasted/"], #isPasted`);
    const spanElements = doc?.querySelectorAll("span:not([id]):has(select)");
    let checkPaste = false;
    if (pastedElements.length > 0) {
      pastedElements.forEach((element) => {
        if (element?.tagName === "SPAN" || element?.tagName === "SELECT") {
          if (element?.tagName === "SPAN") {
            const selectElement = element?.querySelector("select");
            if (selectElement) {
              checkPaste = true;
              element.remove();
            }
          } else if (element?.tagName === "SELECT") {
            checkPaste = true;
            element.remove();
          }
        } else if (element?.tagName === "BR" || element?.tagName === "P") {
          function findNextSpan(node) {
            while (node) {
              if (node?.tagName === "SPAN" || node?.tagName === "SELECT") return node;
              if (node?.tagName === "P") {
                const nestedSpan = node?.querySelector("span");
                if (nestedSpan) return nestedSpan;
                const nestedSelect = node?.querySelector("select");
                if (nestedSelect) return nestedSelect;
              }
              node = node.nextElementSibling;
            }
            return null;
          }
          const targetSpan = findNextSpan(element.nextElementSibling);
          if (targetSpan?.tagName === "SPAN") {
            const selectElement = targetSpan?.querySelector("select");
            if (selectElement) {
              checkPaste = true;
              selectElement.remove();
            }
          } else if (targetSpan?.tagName === "SELECT") {
            checkPaste = true;
            targetSpan.remove();
          }
          else if (element?.tagName === "INPUT") {
            checkPaste = false;
            const newInputUuid = uuid();
            element.id = newInputUuid;
          }
        } else if (element?.tagName === "INPUT") {
          checkPaste = false;
          const newInputUuid = uuid();
          element.id = newInputUuid;
        }
      });
      if (spanElements.length > 0) {
        spanElements.forEach((element) => {
          const selectElement = element?.querySelector("select");
          if (selectElement && !selectElement?.id) {
            checkPaste = true;
            selectElement.remove();
          }
          const buttonElement = element?.querySelector("button");
          if (buttonElement && !buttonElement?.id) {
            checkPaste = true;
            buttonElement.remove();
          }
        });
      }
    }
    const inputs = doc.body.querySelectorAll("input");
    inputs.forEach((element) => {
      if (!element.id || element.id === "isPasted" || document.getElementById(element.id)) {
        let newInputUuid;
        do {
          newInputUuid = uuid();
        } while (document.getElementById(newInputUuid)); // Ensure unique ID
        element.id = newInputUuid;
      }
    });
    if (checkPaste) {
      setState((prevState) => ({ ...prevState, openRemoveDropdown: doc.body.innerHTML }));
    } else {
      setTimeout(() => {
        const updatedContent = editor.current?.editor?.html.get();
        setState(prevState => ({ ...prevState, isFocused: true }))
        onChange(id, updatedContent);
        if (onBlur) onBlur(id, updatedContent);
      }, 0);
    }
  };

  const cleanPastedHtml = (htmlString) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");
    doc.querySelectorAll('span[id^="dropdown_"]').forEach(span => span.remove());
    return doc.body.textContent.trim();
  };

  const handlePasteBefore = (event) => {
    const editorInstance = editor?.current?.editor;
    if (!editorInstance || !event.clipboardData) return;
    const pasteHtml = event.clipboardData.getData("text/html") || event.clipboardData.getData("text/plain");
    const parser = new DOMParser();
    const doc = parser.parseFromString(pasteHtml, "text/html");
    const selects = doc.querySelectorAll("input.froala-dropdown-input");
    const links = doc.querySelectorAll("a");
    const hasURL = /(https?:\/\/[^\s<]+|www\.[^\s<]+[^.,;:"')\]\s<])/gi.test(pasteHtml);
    const isWordContent = /class="?Mso|style="[^"]*mso-|<o:p>|<!--\[if gte mso|xmlns:o="/i.test(pasteHtml);
    const removeWordArtifacts = (node) => {
      const walker = document.createTreeWalker(node, NodeFilter.SHOW_ALL, null, false);
      let current;
      while ((current = walker.nextNode())) {
        if (current.nodeType === Node.COMMENT_NODE) {
          current.remove(); // Remove <!--[if gte mso]> ... <![endif]-->
        } else if (current.nodeType === Node.ELEMENT_NODE) {
          const tag = current.nodeName.toLowerCase();
          // Remove <o:p> and other office-specific tags
          if (tag.startsWith("o:") || tag.startsWith("v:") || tag === "o:p") {
            current.remove();
            continue;
          }
          // Strip mso styles
          if (current.hasAttribute("style")) {
            const cleanedStyle = current
              .getAttribute("style")
              .replace(/mso-[^:;]+:[^;]+;?/gi, "")
              .replace(/background(-color)?:[^;]+;?/gi, "");
            current.setAttribute("style", cleanedStyle);
          }
          // Strip Word classes
          if (current.hasAttribute("class") && /Mso|WordSection/i.test(current.getAttribute("class"))) {
            current.removeAttribute("class");
          }
        }
      }
    };
    removeWordArtifacts(doc.body);
    const cleanedHTML = doc.body.innerHTML.trim();
    if (isWordContent || selects.length > 0 || hasURL) {
      event.preventDefault();
    }
    if (selects.length > 0 && [...selects].every((item) => item.id?.trim() !== "")) {
      event.preventDefault();
      setState((prev) => ({
        ...prev,
        openRemoveDropdown: cleanPastedHtml(cleanedHTML),
      }));
      return false;
    }
    if (isWordContent) {
      setState((prev) => ({
        ...prev,
        openWordPastePopup: {
          raw: pasteHtml,
          clean: cleanPastedHtml(cleanedHTML),
        },
      }));
      return;
    }
    if (hasURL && !links.length) {
      let formattedHtml = cleanedHTML.replace(
        /((https?:\/\/|www\.)[^\s<]+[^.,;:"')\]\s<])/gi,
        '<a href="$1" target="_blank">$1</a>'
      );
      formattedHtml = formattedHtml.replace(/href="www\./g, 'href="http://www.');
      editorInstance?.html?.insert(formattedHtml);
      return;
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setAnchorEl(null);
    };
    const scrollContainer = document.querySelector('.inspection-details');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
    }
    let froalaScrollContainer = null;
    let retryCount = 0;
    const maxRetries = 10;
    const retryDelay = 200;
    let retryTimer;
    const tryAttachFroalaScroll = () => {
      froalaScrollContainer =
        document.querySelector('.fr-scroller') ||
        document.querySelector('.fr-wrapper') ||
        document.querySelector('.fr-element') ||
        document.querySelector('.fr-box.fr-basic');
      if (froalaScrollContainer) {
        froalaScrollContainer.addEventListener('scroll', handleScroll);
      } else if (retryCount < maxRetries) {
        retryCount++;
        retryTimer = setTimeout(tryAttachFroalaScroll, retryDelay);
      } else {
        console.warn("Froala scroll container not found after retries.");
      }
    };
    tryAttachFroalaScroll();
    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
      if (froalaScrollContainer) {
        froalaScrollContainer.removeEventListener('scroll', handleScroll);
      }
      clearTimeout(retryTimer);
    };
  }, []);

  const handleInputBlur = (event, inputId, value) => {
    setTimeout(() => {
      if (event?.target?.tagName === "SELECT") return;
      const editorInstance = editor.current?.editor;
      if (!editorInstance) return;
      let editorContent = editorInstance.html.get();
      if (!editorContent) return;
      if ((!event?.relatedTarget?.id.includes("add-input") || !event?.relatedTarget?.id.includes("add-dropdown")) && !inputId) return;
      const isInput = event?.target?.tagName === "INPUT";
      const isValidTarget = event?.relatedTarget?.id &&
        !["cl_title", "tags-outlined"].some(sub => event.relatedTarget.id.includes(sub));
      let newInputId = isInput
        ? (inputId || uuid())
        : (isValidTarget ? event.relatedTarget.id : (inputId || uuid()));
      let inputValue = isInput
        ? (value?.replace(/"/g, '&quot;') || "")
        : (isValidTarget
          ? (event.relatedTarget?.value?.replace(/"/g, '&quot;') || "")
          : (value?.replace(/"/g, '&quot;') || ""));
      if (!newInputId || inputValue.trim() === undefined) return;
      let inputRegex = new RegExp(
        `(<input[^>]+id="${newInputId}"[^>]*)(value=")([^"]*)(")([^>]*)(title="([^"]*)")?([^>]*)`,
        "g"
      );
      let updatedContent = editorContent.replace(
        inputRegex,
        (match, p1, p2, p3, p4, p5, p6, p7, p8) => {
          let newTitleAttr = p6 ? ` title="${inputValue.trim()}"` : ` title="${inputValue.trim()}"`;
          return `${p1}${p2}${inputValue.trim()}${p4}${p5}${newTitleAttr}${p8}`;
        }
      );
      const parser = new DOMParser();
      const doc = parser.parseFromString(updatedContent, "text/html");
      event.target.setAttribute("value", inputValue.trim());
      event.target.setAttribute("title", inputValue.trim());
      const updatedDropdowns = (() => {
        if (editorData?.dropdownList) return editorData.dropdownList;
        if (id === "commentText" || id === "nonNarrativeComment") return commentData?.comment_dropdowns;
        const fallback = dropdownList?.length ? [...dropdownList] : [];
        return commentData?.summary_comment_dropdowns || fallback;
      })();
      let dropdownIndex = updatedDropdowns?.findIndex((dropdown) => dropdown?.dropdown_uuid === inputId);
      if (dropdownIndex !== -1 && updatedDropdowns) {
        updatedDropdowns[dropdownIndex] = { ...updatedDropdowns[dropdownIndex], selected_value: inputValue };
      }
      if (setEditorContent && updatedDropdowns) {
        if (id === "introTextTE") {
          onBlur?.(id, doc?.body?.innerHTML, { dropdown_uuid: inputId, selected_value: inputValue }, "edit");
          setEditorContent(prevState => ({ ...prevState, innerHTML: doc?.body?.innerHTML, dropdownList: updatedDropdowns }));
        } else if (id === "commentText" || id === "nonNarrativeComment") {
          onBlur?.(id, doc?.body?.innerHTML, updatedDropdowns);
          setEditorContent(prevState => ({ ...prevState, commentText: { innerHTML: doc?.body?.innerHTML, dropdownList: updatedDropdowns }, commentDropdownList: updatedDropdowns }));
        } else if (id === "commentSummary") {
          onBlur?.(id, doc?.body?.innerHTML, updatedDropdowns);
          setEditorContent(prevState => ({ ...prevState, commentSummary: { innerHTML: doc?.body?.innerHTML, dropdownList: updatedDropdowns }, summaryDropdownList: updatedDropdowns }));
        } else if (id === "nonNarrativeComment") {
          onBlur?.(id, doc?.body?.innerHTML, updatedDropdowns);
          setEditorContent(prevState => ({ ...prevState, innerHTML: doc?.body?.innerHTML, dropdownList: updatedDropdowns }));
        } else {
          onBlur?.(id, doc?.body?.innerHTML, updatedDropdowns);
          setEditorContent(prevState => ({ ...prevState, commentText: { innerHTML: doc?.body?.innerHTML, dropdownList: updatedDropdowns }, commentDropdownList: updatedDropdowns }));
        }
      }
    }, 10)
  };

  useEffect(() => {
    const editorInstance = editor?.current?.editor;
    if (!editorInstance) return;
    const contentArea = editorInstance.$el.get(0);
    if (!contentArea) return;
    const handleButtonClick = async (event) => {
      setActiveEditorId?.(event.target.name);
      const button = event.target.closest("button");
      const inputElement = button.previousElementSibling;
      setAnchorEl(inputElement)
      let response;
      if (!button) return;
      const buttonText = button.textContent.trim();
      const inputId = button.id.replace("dropdownButton", "").replace("dropdownIcon_", "");
      if (buttonText === "▾") {
        setIsDropdownLoading(true);
        event.stopPropagation();
        let dropDown;
        if (id === "introTextTE") {
          dropDown = editorData?.dropdownList?.find((e) => e?.dropdown_uuid === inputId);
        } else if (type === "edit") {
          dropDown = dropdownList?.find((e) => e?.dropdown_uuid === inputId);
        }
        if (dropDown === undefined) {
          if (id === "commentText") dropDown = commentData?.comment_dropdowns?.find((e) => e?.dropdown_uuid === inputId);
          else if (id === "commentSummary" && commentData?.summary_comment_dropdowns?.length > 0) dropDown = commentData?.summary_comment_dropdowns?.find((e) => e?.dropdown_uuid === inputId);
          else {
            dropDown = editorData?.dropdownList?.find((e) => e?.dropdown_uuid === inputId);
          }
        }
        let newDropdown;
        if (dropDown === undefined) dropDown = editorData?.dropdownList?.find((e) => e?.dropdown_uuid === inputId);
        if (dropDown?.list_guid) {
          response = await dispatch(getGlobalListById(dropDown?.list_guid));
          if (response?.deleted_at) {
            const editorInstance = editor?.current?.editor;
            let editorContent = editorInstance?.html?.get();
            let newDropdownList = editorData?.dropdownList || ((id === "commentText" || id === "nonNarrativeComment") ? commentData?.comment_dropdowns : commentData?.summary_comment_dropdowns);
            let newIndex = newDropdownList?.findIndex((obj => obj?.list_guid === dropDown?.list_guid))
            newDropdownList[newIndex] = { ...newDropdownList[newIndex], list_guid: undefined, dropdowns_values: response?.options };
            if (id === "introTextTE" && setEditorContent) {
              onBlur?.(id, editorContent, newDropdownList[newIndex], "edit");
              handleDropdownList?.(id, newDropdownList);
              setEditorContent(prevState => ({ ...prevState, dropdownList: newDropdownList }));
            } else if (id === "commentText" && setEditorContent) {
              onBlur?.(id, editorContent, newDropdownList);
              setEditorContent(prevState => ({ ...prevState, commentText: { dropdownList: newDropdownList }, commentDropdownList: newDropdownList }));
            } else if (id === "commentSummary" && setEditorContent) {
              onBlur?.(id, editorContent, newDropdownList);
              setEditorContent(prevState => ({ ...prevState, commentSummary: { dropdownList: newDropdownList }, summaryDropdownList: newDropdownList }));
            } else if (id === "nonNarrativeComment") {
              onBlur?.(id, editorContent, newDropdownList);
              setEditorContent(prevState => ({ ...prevState, dropdownList: newDropdownList }));
            }
            setState((prevState) => ({ ...prevState, listGuid: undefined, listId: undefined, dialogOptionList: newDropdown?.dropdowns_values || [] }));
          }
        } else {
          const getFilteredDropdownValues = (dropdown) =>
            dropdown?.dropdowns_values?.filter(obj => !obj?.toUpperCase()?.includes("XXX")) || [];
          dropdownOptionListRef.current = (() => {
            if (event.target.name === "commentText") {
              return type === "edit"
                ? getFilteredDropdownValues(dropDown)
                : commentOptionList || [];
            }
            if (event.target.name === "commentSummary") {
              return getFilteredDropdownValues(dropDown);
            }
            return dropDown?.dropdowns_values?.length > 0
              ? getFilteredDropdownValues(dropDown)
              : [];
          })();
        }
        const options = (newDropdown?.dropdowns_values || response?.options || dropDown?.dropdowns_values)?.filter(obj => !obj?.toUpperCase()?.includes("XXX")) || [];
        setState(prev => ({ ...prev, dialogOptionList: options.length > 0 ? options : [] }));
        setCurrentInputId(inputId);
        setIsDropdownLoading(false);
        setAnchorEl(button);
      }
      else if (buttonText === "✎") {
        event.stopPropagation();
        handleOpenDialog(event, null, id === "introTextTE" ? commentData?.introductory_text_dropdowns :
          (id === "commentText") ? commentData?.comment_dropdowns :
            editorData?.dropdownList);
      }
    };
    const handleInputBlurWrapper = (event) => {
      if (event.target.tagName === "INPUT") {
        handleInputBlur(event, event.target.id, event.target.value);
      }
    };
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const buttons = node.querySelectorAll('button[id^="dropdownButton"], button[id^="dropdownIcon_"]');
            buttons.forEach(button => {
              button.addEventListener('click', handleButtonClick);
            });
            const inputs = node.querySelectorAll('input');
            inputs.forEach(input => {
              input.addEventListener('blur', handleInputBlurWrapper);
            });
          }
        });
      });
    });
    const buttons = contentArea.querySelectorAll('button[id^="dropdownButton"], button[id^="dropdownIcon_"]');
    buttons.forEach(button => {
      button.addEventListener('click', handleButtonClick);
    });
    const inputs = contentArea.querySelectorAll('input');
    inputs.forEach(input => {
      input.addEventListener('blur', handleInputBlurWrapper);
    });
    observer.observe(contentArea, { childList: true, subtree: true });
    return () => {
      buttons.forEach(button => {
        button.removeEventListener('click', handleButtonClick);
      });
      inputs.forEach(input => {
        input.removeEventListener('blur', handleInputBlurWrapper);
      });
      observer.disconnect();
    };
  }, [editorData, commentData]);

  useEffect(() => {
    const handleDropdownDeleteClick = (event) => {
      if (event.target.id.startsWith("dropdownDelete")) {
        const dropdownId = event.target.id.replace("dropdownDelete", "");
        handleDeleteDropdown(dropdownId);
      }
    };
    document.addEventListener("click", handleDropdownDeleteClick);
    return () => {
      document.removeEventListener("click", handleDropdownDeleteClick);
    };
  }, []);

  const handleDeleteDropdown = (dropdownId) => {
    if (!dropdownId) return;
    const editorInstance = editor?.current?.editor;
    if (!editorInstance) return;
    editorInstance.undo.reset();
    // Parse HTML into DOM
    const wrapper = document.createElement("div");
    wrapper.innerHTML = editorInstance.html.get();
    // Find the exact dropdown span by ID
    const dropdownEl = wrapper.querySelector(`span[id="dropdown_${dropdownId}"], span[id="[dropdown ${dropdownId}]"]`);
    if (dropdownEl) dropdownEl.remove(); // Just remove the target dropdown
    // 🧼 Remove leftover &nbsp; characters around
    wrapper.innerHTML = wrapper.innerHTML.replace(/(&nbsp;|\u00A0)+/g, " ");
    // Set updated HTML back into the editor
    const cleaned = wrapper.innerHTML;
    editorInstance.html.set(cleaned);
    editorInstance.events.focus(true);
    editorInstance.undo.reset();
    setEditorContent(prevState => ({ ...prevState, innerHTML: cleaned }));
    // Filter dropdown from state
    let currentDropdownList =
      id === "introTextTE"
        ? commentData?.introductory_text_dropdowns
        : (id === "commentText" || id === "nonNarrativeComment")
          ? commentData?.comment_dropdowns
          : commentData?.summary_comment_dropdowns;
    const updatedDropdownList = currentDropdownList?.filter((item) => item?.dropdown_uuid !== dropdownId);
    setState((prev) => ({ ...prev, dropdownList: updatedDropdownList, }));
    if (id === "introTextTE") onBlur?.(id, cleaned, { dropdown_uuid: dropdownId }, "delete");
    else if ((id === "commentText" || id === "nonNarrativeComment") && setEditorContent) {
      onBlur?.(id, cleaned, updatedDropdownList);
      setEditorContent(prevState => ({ ...prevState, commentText: { innerHTML: cleaned, dropdownList: updatedDropdownList }, commentDropdownList: updatedDropdownList }));
    } else if (id === "commentSummary" && setEditorContent) {
      onBlur?.(id, cleaned, updatedDropdownList);
      setEditorContent(prevState => ({ ...prevState, commentSummary: { innerHTML: cleaned, dropdownList: updatedDropdownList }, summaryDropdownList: updatedDropdownList }));
    }
    handleDropdownList?.(id, updatedDropdownList);
  };

  useEffect(() => {
    window.openDropdown = (inputId, event) => {
      event.stopPropagation();
      event.preventDefault();
      const input = document.getElementById(inputId);
      if (!input) return;
      setCurrentInputId(inputId);
      const source =
        id === "introTextTE" ? (editorData?.dropdownList || []) :
          id === "commentText" ? (commentData?.comment_dropdowns || []) :
            id === "commentSummary" ? (commentData?.summary_comment_dropdowns || []) :
              (editorData?.dropdownList || []);
      const found = source.find(d => d?.dropdown_uuid === inputId);
      const list = (found?.dropdowns_values || [])
        .filter(v => !String(v).toUpperCase().includes("XXX"));
      dropdownOptionListRef.current = list;
      setState(prev => ({ ...prev, dialogOptionList: list }));
      setAnchorEl(input.nextElementSibling);
    };
    const bindDropdownEvents = () => {
      const dropdownInputs = document.querySelectorAll(".froala-dropdown-input");
      dropdownInputs.forEach((input) => {
        input.onclick = (event) => {
          event.preventDefault();
          setCurrentInputId(input.id);
          if (setActiveEditorId) setActiveEditorId(input.name)
        };
        input.oninput = (event) => {
          if (setActiveEditorId) setActiveEditorId(input.name)
          const value = event.target.value;
          input.value = value;
          setState((prevState) => {
            const prevList = prevState.dropdownList?.length ? prevState.dropdownList : editorData?.dropdownList || [];
            const index = prevList.findIndex((obj) => obj?.dropdown_uuid === event.target.id);
            if (index === -1) return prevState;
            const oldValue = prevList[index]?.selected_value;
            if (oldValue === value) return prevState;
            const newList = [...prevList];
            newList[index] = {
              ...newList[index],
              selected_value: value || ""
            };
            return { ...prevState, dropdownList: newList, dialogOptionList: prevState.dialogOptionList };
          });
        };
      });
    };
    const cleanupRemovedInputs = () => {
      const dropdownInputs = document.querySelectorAll(".froala-dropdown-input");
      const activeIds = new Set(Array.from(dropdownInputs).map((input) => input.id));
      if (currentInputId && !activeIds.has(currentInputId)) {
        setCurrentInputId(null);
        setAnchorEl(null);
      }
    };
    bindDropdownEvents();
    cleanupRemovedInputs();
    window.bindDropdownEvents = bindDropdownEvents;
    window.cleanupRemovedInputs = cleanupRemovedInputs;
    const observer = new MutationObserver(() => {
      bindDropdownEvents();
      cleanupRemovedInputs();
    });
    const froalaEditor = document.querySelector(".fr-element");
    if (froalaEditor) {
      observer.observe(froalaEditor, { childList: true, subtree: true });
    }
    return () => observer.disconnect();
  }, [commentOptionList, dialogOptionList, summaryOptionList, commentData]);

  const handleSelect = (value) => {
    if (!currentInputId) return;
    const inputElement = document.getElementById(currentInputId);
    if (inputElement) {
      inputElement.value = value.replace(/"/g, '&quot;');
    }
    const editorInstance = editor?.current?.editor;
    let newContent;
    setTimeout(() => {
      if (editorInstance) {
        newContent = editorInstance.html.get().replace(
          new RegExp(`(<input[\\s\\S]*?id=["']${currentInputId}["'][\\s\\S]*?value=["'])([\\s\\S]*?)(["'])`, 'i'), `$1${value.replace(/"/g, '&quot;')}$3`
        );
        editorInstance.html.set(newContent);
        editorInstance.events.trigger('contentChanged');
      } else {
      }
    }, 10);
    let updatedDropdownList = [];
    if (type === "edit" && handleChangeDropdown) {
      handleChangeDropdown(inputElement.value, currentInputId, id === "commentText" ? "comment" : "summary", null, null);
    } else {
      if (id === "introTextTE") {
        updatedDropdownList = editorData.dropdownList.map((dropdown) =>
          dropdown?.dropdown_uuid === currentInputId
            ? { ...dropdown, selected_value: value }
            : dropdown
        );
      }
      else if (editorData?.dropdownList?.length > 0) {
        updatedDropdownList = editorData?.dropdownList?.map((dropdown) =>
          dropdown?.dropdown_uuid === currentInputId
            ? { ...dropdown, selected_value: value }
            : dropdown
        );
      } else {
        updatedDropdownList = dropdownList?.map((dropdown) =>
          dropdown?.dropdown_uuid === currentInputId
            ? { ...dropdown, selected_value: value }
            : dropdown
        );
      }
      handleDropdownList?.(id, updatedDropdownList, "select");
      if (id === "introTextTE") {
        onBlur?.(id, newContent, { dropdown_uuid: currentInputId, selected_value: value }, "edit");
        setEditorContent(prevState => ({ ...prevState, innerHTML: { innerHTML: newContent, dropdownList: updatedDropdownList } }));
      } else if (id === "commentText") {
        onBlur?.(id, newContent, updatedDropdownList);
        setEditorContent(prevState => ({ ...prevState, commentText: { ...editorData, innerHTML: newContent, dropdownList: updatedDropdownList }, commentDropdownList: updatedDropdownList }));
      } else if (id === "commentSummary") {
        onBlur?.(id, newContent, updatedDropdownList);
        setEditorContent(prevState => ({ ...prevState, commentSummary: { ...editorData, innerHTML: newContent, dropdownList: updatedDropdownList }, summaryDropdownList: updatedDropdownList }));
      } else if (id === "nonNarrativeComment") {
        setEditorContent(prevState => ({ ...prevState, innerHTML: newContent, dropdownList: updatedDropdownList }));
      }
    }
    setAnchorEl(null);
  };

  const addDropdown = (e, list) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    const target = e?.target;
    const grandParent = target?.parentElement?.parentElement?.parentElement;
    const grandParentPrevSibling = grandParent?.previousElementSibling;
    const editorNameId = grandParentPrevSibling?.id || id;
    if (setActiveEditorId) setActiveEditorId(editorNameId);

    const editorInstance = editor.current?.editor;
    if (!editorInstance) return;

    const editorRoot = editorInstance.$el.get(0);
    const savedRange =
      savedRangeRef.current?.commonAncestorContainer &&
      editorRoot.contains(savedRangeRef.current.commonAncestorContainer)
        ? savedRangeRef.current.cloneRange()
        : null;

    const anchorWrapperCandidate =
      lastFocusedDropdownRef.current ||
      lastFocusedDynamicWrapperRef.current ||
      document.activeElement?.closest?.('span[id^="dropdown_"]') ||
      document.activeElement?.closest?.('.froala-dynamic-input-wrapper');

    const anchorWrapper =
      anchorWrapperCandidate && editorRoot.contains(anchorWrapperCandidate)
        ? anchorWrapperCandidate
        : null;

    const targetListItemEarly = resolveTargetListItem(editorRoot, savedRange, editorInstance);

    const anchorWrapperInList = anchorWrapper?.closest?.("li");
    let selectionPlaced = false;

    if (anchorWrapper && (anchorWrapperInList || !targetListItemEarly)) {
      editorRoot.focus();
      lastFocusedDropdownRef.current = null;
      lastFocusedDynamicWrapperRef.current = null;

      if (anchorWrapper.parentNode) {
        const afterRange = document.createRange();
        let rangeSet = false;
        const nextSibling = anchorWrapper.nextSibling;
        if (nextSibling && nextSibling.nodeType === Node.TEXT_NODE && nextSibling.parentNode) {
          afterRange.setStart(nextSibling, nextSibling.textContent.length);
          rangeSet = true;
        } else if (anchorWrapper.parentNode) {
          afterRange.setStartAfter(anchorWrapper);
          rangeSet = true;
        }
        if (rangeSet) {
          afterRange.collapse(true);
          const sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(afterRange);
          try { editorInstance.selection.setRange(afterRange); } catch (_) {}
          selectionPlaced = true;
        }
      }
    } else if (savedRange) {
      editorRoot.focus();
      try {
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(savedRange);
        try { editorInstance.selection.setRange(savedRange); } catch (_) {}
        selectionPlaced = true;
      } catch (_) {
        editorInstance.selection.restore();
      }
    }

    if (!selectionPlaced && !targetListItemEarly) {
      editorRoot.focus();
      const allWrappers = editorRoot.querySelectorAll(
        '.froala-dynamic-input-wrapper, span[id^="dropdown_"]'
      );
      const lastWrapper = allWrappers.length ? allWrappers[allWrappers.length - 1] : null;
      if (lastWrapper && lastWrapper.parentNode) {
        const fallbackRange = document.createRange();
        const nextSib = lastWrapper.nextSibling;
        if (nextSib && nextSib.nodeType === Node.TEXT_NODE && nextSib.parentNode) {
          fallbackRange.setStart(nextSib, nextSib.textContent.length);
        } else {
          fallbackRange.setStartAfter(lastWrapper);
        }
        fallbackRange.collapse(true);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(fallbackRange);
        try { editorInstance.selection.setRange(fallbackRange); } catch (_) {}
        selectionPlaced = true;
      } else {
        editorInstance.selection.setAtEnd(editorRoot);
        editorInstance.selection.restore();
      }
    }

    const selection = editorInstance.selection.get();

    const selectedNode = selection?.startContainer || selection?.commonAncestorContainer;
    const dropdownAncestor = selectedNode
      ? editorInstance.$(selectedNode).closest('span[id^="dropdown_"]')[0]
      : null;
    if (dropdownAncestor) {
      const range = editorInstance.selection.ranges(0);
      if (range) {
        const afterNode = document.createTextNode("\u00A0");
        dropdownAncestor.parentNode.insertBefore(afterNode, dropdownAncestor.nextSibling);
        range.setStartAfter(afterNode);
        range.setEndAfter(afterNode);
        editorInstance.selection.setRange(range);
      }
    }

    const newDropdownList = (() => {
      if (id === "introTextTE") return commentData?.introductory_text_dropdowns || [];
      if (id === "commentText") return commentData?.comment_dropdowns || [];
      if (id === "commentSummary") return commentData?.summary_comment_dropdowns || [];
      if (editorData?.dropdownList?.length) return editorData.dropdownList;
      if (dropdownList?.length) return [...dropdownList];
      return [];
    })();

    const newSelectUuid = uuid();
    const html = editorInstance.html.get();
    if (html.trim() === "<p><br></p>") {
      editorInstance.html.set("");
    }

    const rangeForListInsert = (() => {
      const sel = window.getSelection();
      if (sel?.rangeCount > 0) {
        const r = sel.getRangeAt(0);
        if (editorRoot.contains(r.commonAncestorContainer)) return r.cloneRange();
      }
      if (savedRange) return savedRange.cloneRange();
      return null;
    })();

    const targetListItem =
      resolveTargetListItem(editorRoot, rangeForListInsert, editorInstance) ||
      targetListItemEarly;

    const wrapperHtml = buildDropdownWrapperHtml(newSelectUuid, editorNameId);
    const temp = document.createElement("div");
    temp.innerHTML = wrapperHtml.trim();
    const wrapperEl = temp.firstElementChild;

    const insertedInList =
      targetListItem &&
      editorRoot.contains(targetListItem) &&
      insertInlineWrapperInListItem(targetListItem, wrapperEl, rangeForListInsert, editorInstance);

    if (!insertedInList) {
      editorInstance.html.insert(wrapperHtml, true);
    }

    normalizeDynamicInputSeparators(editorRoot);
    finalizeWrapperPlacement(
      newSelectUuid,
      ".froala-dropdown-wrapper",
      editorInstance,
      editorRoot,
      targetListItem
    );

    window.bindDropdownEvents?.();
    window.cleanupRemovedInputs?.();
    let newDropodwnObj = {
      dropdown_uuid: newSelectUuid,
      name: list?.name || "",
      selected_value: "",
      dropdowns_values: list?.options,
    }
    if (open === "global_lists" || (saveToGlobalLists && open === "new_list")) newDropodwnObj.list_guid = list?.list_guid;
    newDropdownList?.push(newDropodwnObj);
    setState((prevState) => ({ ...prevState, dropdownList: newDropdownList }));
    if (setIsButtonAdded) setIsButtonAdded(true);
    let updatedHtml = editorInstance.html.get();
    updatedHtml = updatedHtml.replace(/<p>\s*<br\s*\/?>\s*<\/p>/gi, '');
    if (id === "introTextTE" && setEditorContent) {
      onBlur?.(id, updatedHtml, newDropodwnObj, "add");
      handleDropdownList?.(id, newDropdownList);
      setEditorContent(prevState => ({ ...prevState, innerHTML: updatedHtml, dropdownList: newDropdownList }));
    } else if (id === "commentText" && setEditorContent) {
      onBlur?.(id, updatedHtml, newDropdownList);
      setEditorContent(prevState => ({ ...prevState, commentText: { innerHTML: updatedHtml, dropdownList: newDropdownList }, commentDropdownList: newDropdownList }));
    } else if (id === "commentSummary" && setEditorContent) {
      onBlur?.(id, updatedHtml, newDropdownList);
      setEditorContent(prevState => ({ ...prevState, commentSummary: { innerHTML: updatedHtml, dropdownList: newDropdownList }, summaryDropdownList: newDropdownList }));
    } else if (id === "nonNarrativeComment") {
      onBlur?.(id, updatedHtml, newDropdownList);
      setEditorContent(prevState => ({ ...prevState, innerHTML: updatedHtml, dropdownList: newDropdownList }));
    }
  };

  const addInput = (e) => {
  e.preventDefault();
  e.stopPropagation();

  const editorInstance = editor.current?.editor;
  if (!editorInstance) return;

  const editorRoot = editorInstance.$el.get(0);
  const savedRange =
    savedRangeRef.current?.commonAncestorContainer &&
    editorRoot.contains(savedRangeRef.current.commonAncestorContainer)
      ? savedRangeRef.current.cloneRange()
      : null;

  const targetListItemEarly = resolveTargetListItem(editorRoot, savedRange, editorInstance);

  const anchorWrapperCandidate =
    lastFocusedDropdownRef.current ||
    lastFocusedDynamicWrapperRef.current ||
    document.activeElement?.closest?.('span[id^="dropdown_"]') ||
    document.activeElement?.closest?.('.froala-dynamic-input-wrapper');

  const anchorWrapper = anchorWrapperCandidate && editorRoot.contains(anchorWrapperCandidate)
    ? anchorWrapperCandidate
    : null;

  const anchorWrapperInList = anchorWrapper?.closest?.("li");

  let selectionPlaced = false;

  if (anchorWrapper && (anchorWrapperInList || !targetListItemEarly)) {
    editorInstance.$el.get(0).focus();
    lastFocusedDropdownRef.current = null;
    lastFocusedDynamicWrapperRef.current = null;

    if (anchorWrapper.parentNode) {
      const afterRange = document.createRange();
      let rangeSet = false;
      const nextSibling = anchorWrapper.nextSibling;
      if (nextSibling && nextSibling.nodeType === Node.TEXT_NODE && nextSibling.parentNode) {
        afterRange.setStart(nextSibling, nextSibling.textContent.length);
        rangeSet = true;
      } else if (anchorWrapper.parentNode) {
        afterRange.setStartAfter(anchorWrapper);
        rangeSet = true;
      }
      if (rangeSet) {
        afterRange.collapse(true);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(afterRange);
        try { editorInstance.selection.setRange(afterRange); } catch (_) {}
        selectionPlaced = true;
      }
    }
  } else if (savedRange) {
    editorRoot.focus();
    try {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedRange);
      try { editorInstance.selection.setRange(savedRange); } catch (_) {}
      selectionPlaced = true;
    } catch (_) {
      editorInstance.selection.restore();
    }
  }

  if (!selectionPlaced && !targetListItemEarly) {
    editorInstance.$el.get(0).focus();
    const allWrappers = editorRoot.querySelectorAll('.froala-dynamic-input-wrapper');
    const lastWrapper = allWrappers.length ? allWrappers[allWrappers.length - 1] : null;
    if (lastWrapper && lastWrapper.parentNode) {
      const fallbackRange = document.createRange();
      const nextSib = lastWrapper.nextSibling;
      if (nextSib && nextSib.nodeType === Node.TEXT_NODE && nextSib.parentNode) {
        fallbackRange.setStart(nextSib, nextSib.textContent.length);
      } else {
        fallbackRange.setStartAfter(lastWrapper);
      }
      fallbackRange.collapse(true);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(fallbackRange);
    } else {
      editorInstance.selection.setAtEnd(editorRoot);
      editorInstance.selection.restore();
    }
  }

  const newInputUuid = uuid();

  const rangeForListInsert = (() => {
    const sel = window.getSelection();
    if (sel?.rangeCount > 0) return sel.getRangeAt(0).cloneRange();
    if (savedRange) return savedRange.cloneRange();
    return null;
  })();

  const targetListItem =
    anchorWrapperInList ||
    resolveTargetListItem(editorRoot, rangeForListInsert, editorInstance) ||
    targetListItemEarly;

  const wrapperHtml = `${buildDynamicInputWrapperHtml(newInputUuid)}\u00A0`;

  const insertedInList =
    targetListItem &&
    editorRoot.contains(targetListItem) &&
    insertDynamicInputInListItem(targetListItem, newInputUuid, rangeForListInsert, editorInstance);

  if (!insertedInList) {
    editorInstance.html.insert(wrapperHtml, true);
  }

  normalizeDynamicInputSeparators(editorRoot);

  finalizeWrapperPlacement(
    newInputUuid,
    ".froala-dynamic-input-wrapper",
    editorInstance,
    editorRoot,
    targetListItem
  );

  setIsButtonAdded?.(true);

  requestAnimationFrame(() => {
    const inputEl = document.getElementById(newInputUuid);
    const editorRoot = editorInstance.$el?.get(0);

    if (!inputEl) return;

    // IMPORTANT: remember this wrapper for next insertion
    lastFocusedDynamicWrapperRef.current =
      inputEl.closest(".froala-dynamic-input-wrapper");

    inputEl.focus({ preventScroll: true });
    inputEl.select();

    const handleChange = (event) => {
      // Sync the HTML attribute so Froala's html.get() captures the typed value.
      event.target.setAttribute('value', event.target.value);
      document.dispatchEvent(
        new CustomEvent("changeInput", {
          detail: {
            id: newInputUuid,
            value: event.target.value,
          },
        })
      );
      // Notify Froala so onChange is called with the updated HTML (input elements
      // are contenteditable=false so they don't trigger contentChanged on their own).
      editor.current?.editor?.events.trigger('contentChanged');
    };

    const handleKeydown = (keyEvent) => {
      if (keyEvent.key === "Enter") {
        const wrapper = inputEl.closest(".froala-dynamic-input-wrapper");
        if (wrapper && editorRoot?.contains(wrapper)) {
          keyEvent.preventDefault();
          keyEvent.stopImmediatePropagation();
          inputEl.blur();
          editorRoot.focus();
          let liveWrapper = wrapper;
          if (!document.contains(liveWrapper) || !liveWrapper.parentNode) {
            const freshInput = document.getElementById(newInputUuid);
            liveWrapper = freshInput ? freshInput.closest(".froala-dynamic-input-wrapper") : null;
          }
          const sel = liveWrapper && liveWrapper.parentNode ? window.getSelection() : null;
          if (sel) {
            const newRange = document.createRange();
            const trailingSeparator =
              liveWrapper.nextSibling &&
              liveWrapper.nextSibling.nodeType === Node.TEXT_NODE &&
              liveWrapper.nextSibling.parentNode
                ? liveWrapper.nextSibling
                : null;
            if (trailingSeparator) {
              newRange.setStart(trailingSeparator, trailingSeparator.textContent.length);
            } else {
              liveWrapper.after(document.createTextNode(" "));
              newRange.setStart(liveWrapper.nextSibling, 1);
            }
            newRange.collapse(true);
            sel.removeAllRanges();
            sel.addRange(newRange);
            savedRangeRef.current = newRange.cloneRange();
          }
        }
        return;
      }
      if (keyEvent.key === "Backspace" && inputEl.value === "") {
        keyEvent.preventDefault();

        const wrapper = inputEl.closest(
          ".froala-dynamic-input-wrapper"
        );

        if (!wrapper) return;

        detachDynamicInputListeners(newInputUuid);

        const anchorNode = wrapper.previousSibling;
        const parentNode = wrapper.parentNode;

        wrapper.remove();

        const activeEditor = editor.current?.editor;

        if (activeEditor) {
          placeCaretInEditorAt(anchorNode, parentNode);

          activeEditor.events.trigger("contentChanged");

          const updatedContent = activeEditor.html.get();

          if (id === "introTextTE" && setEditorContent) {
            setEditorContent((prev) => ({
              ...prev,
              innerHTML: updatedContent,
            }));
          } else if (
            id === "commentText" &&
            setEditorContent
          ) {
            setEditorContent((prev) => ({
              ...prev,
              commentText: {
                ...editorData,
                innerHTML: updatedContent,
              },
            }));
          } else if (
            id === "commentSummary" &&
            setEditorContent
          ) {
            setEditorContent((prev) => ({
              ...prev,
              commentSummary: {
                ...editorData,
                innerHTML: updatedContent,
              },
            }));
          } else if (id === "nonNarrativeComment") {
            setEditorContent((prev) => ({
              ...prev,
              innerHTML: updatedContent,
            }));
          }

          onBlur?.(id, updatedContent);
        }
      }
    };

    ensureDynamicInputObserver(editorRoot);

    registerDynamicInputListeners(
      inputEl,
      newInputUuid,
      handleKeydown,
      handleChange
    );

    // Delay state updates until AFTER focus succeeds
    setTimeout(() => {
      const editorContent = editorInstance.html.get();

      onBlur?.(id, editorContent);

      if (id === "introTextTE" && setEditorContent) {
        setEditorContent((prev) => ({
          ...prev,
          innerHTML: editorContent,
        }));
      } else if (
        id === "commentText" &&
        setEditorContent
      ) {
        setEditorContent((prev) => ({
          ...prev,
          commentText: {
            ...editorData,
            innerHTML: editorContent,
          },
        }));
      } else if (
        id === "commentSummary" &&
        setEditorContent
      ) {
        setEditorContent((prev) => ({
          ...prev,
          commentSummary: {
            ...editorData,
            innerHTML: editorContent,
          },
        }));
      } else if (id === "nonNarrativeComment") {
        setEditorContent((prev) => ({
          ...prev,
          innerHTML: editorContent,
        }));
      }
    }, 0);
  });
};

  const updateDropdownContent = (newDropdownOptions, replacedContent, newDialogOptionList, listGuid) => {
    const editorInstance = editor?.current?.editor;
    if (setEditorContent && id === "introTextTE") {
      onBlur?.(id, replacedContent, { dropdown_uuid: selectedDropdown, dropdowns_values: newDialogOptionList, list_guid: listGuid }, "edit");
      handleDropdownList?.(id, newDropdownOptions);
      setEditorContent(prevState => ({ ...prevState, innerHTML: replacedContent, dropdownList: newDropdownOptions }));
    } else if (id === "commentSummary") {
      onBlur?.(id, replacedContent, newDropdownOptions);
      if (setEditorContent) setEditorContent(prevState => ({
        ...prevState,
        commentSummary: { innerHTML: replacedContent, dropdownList: newDropdownOptions },
        summaryDropdownList: newDropdownOptions
      }));
      else {
        handleDropdownList?.(id, newDropdownOptions);
        onChange?.(id, editorInstance.html.get());
      }
    } else if (id === "nonNarrativeComment") {
      onBlur?.(id, replacedContent, newDropdownOptions);
      if (setEditorContent) setEditorContent(prevState => ({ ...prevState, innerHTML: replacedContent, dropdownList: newDropdownOptions }));
    } else {
      onBlur?.(id, replacedContent, newDropdownOptions);
      if (setEditorContent) setEditorContent(prevState => ({
        ...prevState,
        commentText: { innerHTML: replacedContent, dropdownList: newDropdownOptions },
        commentDropdownList: newDropdownOptions
      }));
      if (type !== "edit" && setCommentOptionList) setCommentOptionList(newDialogOptionList);
      else {
        handleDropdownList?.(id, newDropdownOptions);
        onChange?.(id, editorInstance.html.get());
      }
    }
  }
  const resetState = (newDropdownOptions = [], newDialogOptionList = []) => {
    setState(p => ({
      ...p,
      name: "",
      open: false,
      description: "",
      tags: [],
      dialogOptionList: newDialogOptionList,
      saveToGlobalLists: false,
      newOption: "",
      textfield: false,
      editedOption: null,
      dropdownList: newDropdownOptions,
      summaryOptionList: [],
      listId: null,
      listGuid: null
    }));
  }
  const handleSave = (event) => {
    event?.stopPropagation();
    const filteredDialogOptionList = (dialogOptionList || [])
      .map(option => (option || "").toString().trim())
      .filter(option => option !== "" && option !== null && option !== undefined);
    const editorInstance = editor?.current?.editor;
    let editorContent = editorInstance?.html?.get();
    const newDialogOptionList = filteredDialogOptionList;
    const updateCurrentDropdown = (newId) => {
      const inputId = selectedDropdown?.replace("dropdownButton", "").replace("dropdownIcon_", "");
      let newDropdownOptions =
        dropdownList?.length ? [...dropdownList] : id === "introTextTE" ? commentData?.introductory_text_dropdowns : editorData?.dropdownList || [];
      let index = newDropdownOptions?.findIndex((obj) => obj?.dropdown_uuid === inputId);
      if (index === -1) index = dropdownList?.findIndex((obj) => obj?.isPasted === true);
      if (newDropdownOptions.length === 0) {
        newDropdownOptions.push({
          list_guid: newId || undefined,
          dropdown_uuid: newId || inputId,
          selected_value: "",
          dropdowns_values: newDialogOptionList?.length === 0 ? [] : newDialogOptionList
        });
      } else {
        newDropdownOptions[index] = {
          ...newDropdownOptions[index],
          list_guid: newId || undefined,
          dropdowns_values: newDialogOptionList || [],
        };
      }
      return { newDropdownOptions, editorContent, newDialogOptionList };
    };
    if (!saveToGlobalLists && open === "new_list") {
      addDropdown(event, { options: filteredDialogOptionList });
      setState((prevState) => ({ ...prevState, name: "", open: false, description: "", tags: [], dialogOptionList: [] }));
    } else if (open === "edit_list" && saveToGlobalLists) {
      const newId = uuid();
      const data = {
        options: filteredDialogOptionList,
        list_guid: listGuid || newId,
        name: name?.length > 255 ? name?.slice(0, 255) : name,
        description: description,
        tag_ids: tags
      }
      if (listGuid) {
        dispatch(editGlobalList({ ...data, tag_ids: tags?.map((tag) => tag?.pivot?.tag_id || tag?.id) }));
        resetState();
      }
      else {
        dispatch(addGlobalList({ ...data, tag_ids: tags?.map((tag) => tag?.pivot?.tag_id || tag?.id) }));
        const { newDropdownOptions, editorContent, newDialogOptionList } = updateCurrentDropdown(newId);
        updateDropdownContent(newDropdownOptions, editorContent, newDialogOptionList, newId);
        resetState(newDropdownOptions, newDialogOptionList);
      }
    } else if (saveToGlobalLists) {
      const newId = uuid();
      const data = {
        options: filteredDialogOptionList,
        list_guid: listGuid || newId,
        name: name?.length > 255 ? name?.slice(0, 255) : name,
        description: description,
        tag_ids: tags
      }
      dispatch(addGlobalList({ ...data, tag_ids: tags?.map((tag) => tag?.pivot?.tag_id || tag?.id) }));
      addDropdown(event, data);
      resetState();
    } else {
      let newDropdownOptions =
        dropdownList?.length ? [...dropdownList] : id === "introTextTE" ? commentData?.introductory_text_dropdowns : editorData?.dropdownList || [];
      let index = newDropdownOptions?.findIndex((obj) => obj?.dropdown_uuid === selectedDropdown);
      if (index === -1) index = dropdownList?.findIndex((obj) => obj?.isPasted === true);
      const editorInstance = editor?.current?.editor;
      let editorContent = editorInstance?.html?.get();
      const newDialogOptionList = filteredDialogOptionList;
      const optionElements = filteredDialogOptionList?.map((option) => {
        const truncatedOption = option?.length > 40 ? option?.substring(0, 40) + "..." : option;
        const isSelected = newDropdownOptions[index]?.selected_value === option;
        const newOption = option.replace(/"/g, '&quot;')
        return `<option title = "${newOption}" value = "${newOption}" ${isSelected ? "selected" : ""}> ${truncatedOption}</option > `;
      });
      const optionsString = optionElements.join("");
      const selectRegex = new RegExp(`<input[^>]*?id=["']${selectedDropdown}["'][\\s\\S]*?<\\/input>.*?`, "g");
      function replaceOptionsInSelect(selectHtml, newOptionsHtml) {
        const optionsRegex = /(<select[^>]*>)[\s\S]*?(<\/select>)/;
        return selectHtml.replace(optionsRegex, `$1${newOptionsHtml}$2`);
      }
      const newOptionsHtml = `${optionsString}`;
      const replacedContent = editorContent?.replace(selectRegex, (match) => replaceOptionsInSelect(match, newOptionsHtml));
      editorInstance.html.set(replacedContent);
      if (index === -1) {
        newDropdownOptions.push({
          dropdown_uuid: selectedDropdown,
          selected_value: "",
          dropdowns_values: newDialogOptionList?.length === 0 ? [] : newDialogOptionList
        });
      } else {
        newDropdownOptions[index] = {
          ...newDropdownOptions[index],
          dropdowns_values: newDialogOptionList || [],
        };
      }
      const list = newDropdownOptions[index]?.dropdowns_values || [];
      dropdownOptionListRef.current = list;
      updateDropdownContent(newDropdownOptions, editorContent, newDialogOptionList);
      setState(p => ({
        ...p,
        newOption: "",
        textfield: false,
        editedOption: null,
        open: false,
        dialogOptionList: filteredDialogOptionList,
        dropdownList: newDropdownOptions,
        summaryOptionList: id === "commentSummary" ? newDialogOptionList : [],
        listId: null,
        listGuid: null
      }));
    }
  };

  const openHelpDialog = () => {
    setState((prevState) => ({ ...prevState, openHelp: !openHelp }));
    setShowAIModal(!showAIModal);
  };

  Froalaeditor.DefineIcon("aiHelper", { SRC: BulbIcon, ALT: "Image button", height: 50, width: "50px", template: "image" });
  Froalaeditor.RegisterCommand("aiHelper", {
    title: "AI helper",
    focus: false,
    undo: true,
    refreshAfterCallback: true,
    callback: function () {
      openHelpDialog();
    },
  });

  const toolbarButtonsConfig = {
    moreText: { buttons: ["bold", "italic", "underline", "fontFamily", "fontSize", "textColor", "backgroundColor", "alignLeft", "alignCenter", "alignRight"], align: "left", buttonsVisible: 3 },
    moreRich: { buttons: ["insertLink", "emoticons", "specialCharacters", "fontAwesome", "formatOLSimple", "formatUL", "outdent", "indent"], align: "left", buttonsVisible: 0 },
    moreMisc: { buttons: ["undo", "redo"], align: "right", buttonsVisible: 2 },
  };

  if (showAI) {
    toolbarButtonsConfig.aiHelper = { buttons: ["aiHelper"], align: "left" };
  }

  const setEditorData = (data, type) => {
    const editorInstance = editor?.current?.editor;
    if (!editorInstance) return;
    editorInstance.undo.reset();
    const parser = new DOMParser();
    const document = parser.parseFromString(editorInstance?.html?.get(), "text/html");
    document.querySelectorAll("span").forEach((span) => {
      const hasSelect = span.querySelector("select");
      const hasButton = span.querySelector("button");
      const otherContent = span.cloneNode(true);
      otherContent.querySelectorAll("select, button, input").forEach((child) => child.remove());
      if (otherContent.innerHTML.trim()) {
        const pTag = document.createElement("p");
        pTag.innerHTML = otherContent.innerHTML;
        span.parentNode.insertBefore(pTag, span.nextSibling);
      }
      if (!hasSelect?.id || !hasButton?.id) {
        hasSelect?.remove();
        hasButton?.remove();
        span.remove();
      }
    });
    document.querySelectorAll("select:not([id]), select[id='isPasted']").forEach((select) => {
      let nextSibling = select.nextElementSibling;
      if (!select?.id) select.remove();
      while (nextSibling && nextSibling.nodeType === 1) {
        if (nextSibling?.tagName.toLowerCase() === 'button') {
          nextSibling.remove();
          break;
        }
        nextSibling = nextSibling.nextElementSibling;
      }
    });
    document.querySelectorAll("p, div, span").forEach((el) => {
      if (!el.textContent.trim()) {
        el.remove();
      }
    });
    const pastedElements = document?.querySelectorAll(`[id^="isPasted"], [id^="pasted/"], #isPasted`);
    if (type === "cancel") {
      if (pastedElements?.length > 0) {
        const pastedDoc = parser.parseFromString(data, "text/html");
        pastedElements.forEach((element) => {
          if (element?.tagName === "BR" || element?.tagName === "P") {
            function findNextSpan(node) {
              while (node) {
                if (node?.tagName === "SPAN" || node?.tagName === "SELECT") return node;
                if (node?.tagName === "P") {
                  const nestedSpan = node?.querySelector("span");
                  if (nestedSpan) return nestedSpan;
                  const nestedSelect = node?.querySelector("select");
                  if (nestedSelect) return nestedSelect;
                }
                node = node.nextElementSibling;
              }
              return null;
            }
            const targetSpan = findNextSpan(element.nextElementSibling);
            if (targetSpan?.tagName === "SPAN") {
              const selectElement = targetSpan?.querySelector("select");
              if (selectElement) {
                selectElement.remove();
              }
            } else if (targetSpan?.tagName === "SELECT") {
              targetSpan.remove();
            }
          }
        })
        pastedDoc.querySelectorAll("select:not([id])").forEach((select) => {
          let nextSibling = select.nextElementSibling;
          if (!select?.id) select.remove();
          while (nextSibling && nextSibling.nodeType === 1) {
            if (nextSibling?.tagName.toLowerCase() === 'button') {
              nextSibling.remove();
              break;
            }
            nextSibling = nextSibling.nextElementSibling;
          }
        });
        pastedDoc.querySelectorAll("button:not([id])").forEach((button) => {
          if (!button?.id) button.remove();
        });
        onChange(id, pastedDoc.body.innerHTML);
        editorInstance?.html?.set(pastedDoc.body.innerHTML);
      } else {
        const newDoc = parser.parseFromString(data, "text/html");
        onChange(id, newDoc.body.innerHTML);
        editorInstance?.html?.set(newDoc.body.innerHTML);
      }
    } else {
      if (pastedElements?.length > 0) {
        pastedElements.forEach((element) => {
          const textContent = element.textContent.trim();
          if (textContent) {
            const textNode = document.createTextNode(textContent);
            element.parentNode.replaceChild(textNode, element);
          } else {
            element.remove();
          }
        });
      }
      onChange(id, document.body.innerHTML);
      editorInstance?.html?.set(document.body.innerHTML);
    }
  };

  const handleCloseRemoveDropdown = () => {
    setEditorData(editorData?.innerHTML, "cancel");
    setState((prevState) => ({ ...prevState, openRemoveDropdown: false }));
  };

  const handleSubmitRemoveDropdown = () => {
    const editorInstance = editor?.current?.editor;
    if (!editorInstance) return;
    const copiedData = state.openRemoveDropdown;
    if (!copiedData) return;
    setEditorData(null, "paste");
    editorInstance.html.insert(copiedData);
    setState((prevState) => ({ ...prevState, openRemoveDropdown: false }));
  };

  document.addEventListener("click", function (event) {
    if (event.target.id.startsWith("dropdownIcon_")) {
      let dropdownId = event.target.id.replace("dropdownIcon_", "");
      window.openDropdown(dropdownId, event);
    }
  });

  const handleLinkValidation = function (link, text, attrs) {
    const urlPattern = /^(https?:\/\/)([\w\-]+\.)+[\w\-]+(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/i;
    const testResult = urlPattern.test(link);
    if (testResult) {
      return true
    } else {
      toast.error("Please enter a valid URL.")
      return false;
    }
  };
  const handleOutsideClick = async () => {
    const editorInstance = editor.current?.editor;
    const editorContent = editorInstance?.html?.get();
    if (onBlur && storeData?.editorListner) {
      await onBlur(id, editorContent);
      setState((prevState) => ({ ...prevState, isFocused: false }));
    }
  };

  const saveEditorSelection = () => {
    const editorRoot = editor.current?.editor?.$el?.get(0);
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && editorRoot) {
      const range = sel.getRangeAt(0);
      if (editorRoot.contains(range.commonAncestorContainer)) {
        savedRangeRef.current = range.cloneRange();
        let node = range.startContainer;
        if (node?.nodeType === Node.TEXT_NODE) node = node.parentElement;
        const insideWrapper = node?.closest?.(
          '.froala-dynamic-input-wrapper, span[id^="dropdown_"]'
        );
        if (!insideWrapper) {
          lastFocusedDynamicWrapperRef.current = null;
          lastFocusedDropdownRef.current = null;
        }
      }
    }
  };

  const preserveEditorSelectionForList = (e) => {
    e.preventDefault();
    const active = document.activeElement;
    if (active?.closest?.(".froala-dynamic-input-wrapper")) {
      lastFocusedDynamicWrapperRef.current = active.closest(".froala-dynamic-input-wrapper");
    }
    if (active?.closest?.('span[id^="dropdown_"]')) {
      lastFocusedDropdownRef.current = active.closest('span[id^="dropdown_"]');
    }
    saveEditorSelection();
  };

  const handleButtonClicks = (e, type) => {
    const grandParent = e.target.parentElement.parentElement;
    const grandParentPrevSibling = grandParent.previousElementSibling;
    if (setActiveEditorId) setActiveEditorId(grandParentPrevSibling?.id);
    saveEditorSelection();
    setAnchorEl(null);
    setCurrentInputId(null);
    setState((prevState) => ({ ...prevState, open: type, dialogOptionList: [] }))
  }

  return (
    <div id={id} style={{ width: "100%" }}>
      {!open ? <Popper open={Boolean(anchorEl && document.body.contains(anchorEl))}
        anchorEl={document.body.contains(anchorEl) ? anchorEl : null}
        placement="bottom-end"
        sx={{
          zIndex: 10000,
          '&[data-popper-placement^="bottom"]': { marginTop: '1px !important' },
          '&[data-popper-placement^="top"]': { marginBottom: '1px !important' }
        }}
        modifiers={[{ name: 'flip', options: { fallbackPlacements: ['bottom-end', 'top-start', 'top-end'] } },
        { name: 'preventOverflow', options: { mainAxis: true, altAxis: true } },
        ]}
        id={`popper-${id}`}
      >
        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
          <Paper key={`dropdown-${id}-${currentInputId}`}
            style={{ position: "relative", left: isDropdownLoading ? "22px" : "0px", width: "242px", minHeight: "20px", height: "auto", maxHeight: "200px", overflow: isDropdownLoading ? "hidden" : "auto", textAlign: isDropdownLoading ? "center" : null, textOverflow: "ellipsis" }}>
            {isDropdownLoading ? <CircularProgress /> : dialogOptionList?.length === 0 ? <MenuItem sx={{ fontSize: "14px", padding: "12px 4px 12px 8px", fontFamily: "Open Sans", letterSpacing: 0 }}>{"     "}</MenuItem> :
              dialogOptionList?.map((e, index) => (
                <MenuItem key={index} sx={{
                  fontSize: "14px",
                  padding: "5px 4px 5px 8px",
                  fontFamily: "Open Sans",
                  letterSpacing: 0,
                  color: "#3B3B3B",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  maxWidth: "241px",
                  display: "block",
                  minHeight: "auto"
                }} onClick={() => handleSelect(e)}>{String(e)}</MenuItem>
              ))
            }
          </Paper>
        </ClickAwayListener>
      </Popper> : null}
      <div id={id} className="editor-class" style={{ width: "100%", color: "#fff", isolation: "isolate" }}>
        <ClickAwayListener mouseEvent="onMouseDown" touchEvent="onTouchStart" onClickAway={() => isFocused && handleOutsideClick()}>
          <FroalaEditor
            id={id}
            ref={editor}
            value={editorData}
            onModelChange={handleModelChange}
            config={{
              key: "zEG4iE3C7A8A4C4A3A4aIVLEABVAYFKc2Cb1MYGH1g1NYVMiG5G4E3A2A1C8C6D4D4C4==",
              spellCheck: true,
              imagePaste: false,
              htmlUntouched: true,
              emptyBlocks: false,
              charCounterCount: false,
              quickInsertEnabled: false,
              pastePlain: true,
              pasteDeniedAttrs: ['style', 'class', 'id'],
              pasteDeniedTags: ['style'],
              wordPasteModal: false,
              toolbarButtons: toolbarButtonsConfig,
              htmlAllowedTags: ['input', 'span', 'button', 'select', 'option', 'strong', 'u', 'i', 'em', 'a', 'br', 'p', 'div', 'li', 'ol', 'ul', 'blockquote'],
              linkAutoPrefix: "https://",
              linkAlwaysBlank: true,
              pluginsEnabled: ["wordPaste", "spellChecker", "codeBeautifier", "fontFamily", "fontSize", "colors", "align", "lists"],
              ...froalaConfig,
              events: {
                ...(froalaConfig?.events || {}),
                'link.beforeInsert': handleLinkValidation,
                "paste.before": handlePasteBefore,
                'focus': function () {
                  froalaConfig?.events?.['focus']?.call(this);
                  if (onFocusEditor) onFocusEditor();
                },
                'blur': function () {
                  froalaConfig?.events?.['blur']?.call(this);
                  saveEditorSelection();
                },
                'keyup': function () {
                  froalaConfig?.events?.['keyup']?.call(this);
                  saveEditorSelection();
                },
                'mouseup': function () {
                  froalaConfig?.events?.['mouseup']?.call(this);
                  saveEditorSelection();
                },
                'keydown': function (e) {
                  froalaConfig?.events?.['keydown']?.call(this, e);
                  if (e.key === 'Backspace') {
                    // When a dynamic input is focused and empty, remove the wrapper
                    if (
                      e.target.tagName === 'INPUT' &&
                      e.target.closest('.froala-dynamic-input-wrapper') &&
                      e.target.value === ''
                    ) {
                      e.preventDefault();
                      e.stopPropagation();
                      const wrapper = e.target.closest('.froala-dynamic-input-wrapper');
                      detachDynamicInputListeners(e.target.id);
                      const leadingSep = wrapper.previousSibling;
                      const trailingSep = wrapper.nextSibling;
                      const anchorNode = (leadingSep && leadingSep.nodeType === Node.TEXT_NODE && /^[\s\u00A0]*$/.test(leadingSep.textContent) && !leadingSep.previousSibling?.classList?.contains('froala-dynamic-input-wrapper'))
                        ? leadingSep.previousSibling
                        : leadingSep;
                      const parentNode = wrapper.parentNode;
                      wrapper.remove();
                      if (trailingSep && trailingSep.nodeType === Node.TEXT_NODE && /^[\s\u00A0]*$/.test(trailingSep.textContent)) {
                        trailingSep.remove();
                      }
                      if (leadingSep && leadingSep.nodeType === Node.TEXT_NODE && /^[\s\u00A0]*$/.test(leadingSep.textContent) && !leadingSep.previousSibling?.classList?.contains('froala-dynamic-input-wrapper')) {
                        leadingSep.remove();
                      }
                      normalizeDynamicInputSeparators(this.$el?.get(0));
                      placeCaretInEditorAt(anchorNode, parentNode);
                      this.events.trigger('contentChanged');
                      const updatedContent = this.html.get();
                      onBlur?.(id, updatedContent);
                      if (id === "introTextTE" && setEditorContent) setEditorContent(prev => ({ ...prev, innerHTML: updatedContent }));
                      else if (id === "commentText" && setEditorContent) setEditorContent(prev => ({ ...prev, commentText: { ...editorData, innerHTML: updatedContent } }));
                      else if (id === "commentSummary" && setEditorContent) setEditorContent(prev => ({ ...prev, commentSummary: { ...editorData, innerHTML: updatedContent } }));
                      else if (id === "nonNarrativeComment" && setEditorContent) setEditorContent(prev => ({ ...prev, innerHTML: updatedContent }));
                      return;
                    }
                    // When the caret in the editor is immediately after a dynamic input wrapper, remove it
                    const sel = window.getSelection();
                    if (sel && sel.rangeCount > 0) {
                      const range = sel.getRangeAt(0);
                      if (range.collapsed) {
                        const { startContainer, startOffset } = range;
                        let prevNode = startContainer.nodeType === Node.ELEMENT_NODE
                          ? startContainer.childNodes[startOffset - 1]
                          : startOffset === 0 ? startContainer.previousSibling : null;
                        // Case 1: caret is directly after a wrapper → delete wrapper
                        if (prevNode && prevNode.classList?.contains('froala-dynamic-input-wrapper')) {
                          e.preventDefault();
                          const anchorNode = prevNode.previousSibling;
                          const parentNode = prevNode.parentNode;
                          const trailingSep = prevNode.nextSibling;
                          const inputId = prevNode.querySelector('input')?.id;
                          if (inputId) detachDynamicInputListeners(inputId);
                          prevNode.remove();
                          if (trailingSep && trailingSep.nodeType === Node.TEXT_NODE && /^[\s\u00A0]*$/.test(trailingSep.textContent)) {
                            trailingSep.remove();
                          }
                          normalizeDynamicInputSeparators(this.$el?.get(0));
                          placeCaretInEditorAt(anchorNode, parentNode);
                          this.events.trigger('contentChanged');
                        }
                        // Case 2: caret is in text after a &nbsp; separator that sits right after a wrapper
                        // Delete both the separator and the wrapper so one backspace removes the whole input block
                        else if (
                          prevNode &&
                          prevNode.nodeType === Node.TEXT_NODE &&
                          /^[\s\u00A0]*$/.test(prevNode.textContent) &&
                          prevNode.previousSibling?.classList?.contains('froala-dynamic-input-wrapper')
                        ) {
                          e.preventDefault();
                          const wrapper = prevNode.previousSibling;
                          const anchorNode = wrapper.previousSibling;
                          const parentNode = wrapper.parentNode;
                          const inputId = wrapper.querySelector('input')?.id;
                          if (inputId) detachDynamicInputListeners(inputId);
                          prevNode.remove();
                          wrapper.remove();
                          normalizeDynamicInputSeparators(this.$el?.get(0));
                          placeCaretInEditorAt(anchorNode, parentNode);
                          this.events.trigger('contentChanged');
                        }
                      }
                    }
                  }
                },
              },
            }
            }
          />
        </ClickAwayListener>
        {(location.pathname.includes("templates") && ((isNarrativeTemplate && id === "introTextTE") || (id === "commentText" || id === "commentSummary" || id === "nonNarrativeComment"))) ? (
          <Grid display={"flex"} className="add-dropdown-box">
            <Button
              className="add-dropdown-typo"
              id="add-input"
              onMouseDown={(e) => e.preventDefault()}
              onClick={(event) => addInput(event)}
              disableRipple
              startIcon={<TextFieldsIcon className="add-dropdown-circle" />}
            >
              Add input
            </Button>
            <>
              <Button
                className="add-dropdown-typo"
                id="add-dropdown"
                onMouseDown={preserveEditorSelectionForList}
                onClick={(e) => handleButtonClicks(e, "global_lists")}
                disableRipple
                startIcon={< MenuBookIcon className="add-dropdown-circle" />}
              >
                Global lists
              </Button>
              <Button
                className="add-dropdown-typo"
                id="add-dropdown"
                onMouseDown={preserveEditorSelectionForList}
                onClick={(e) => handleButtonClicks(e, "new_list")}
                disableRipple
                startIcon={<AddCircleOutlineOutlinedIcon className="add-dropdown-circle" />}
              >
                New list
              </Button>
            </>
          </Grid>
        ) : (id === "" || id === "commentText" || id === "commentSummary" || (id === "nonNarrativeComment" && AllCategoryList?.template?.is_hip_template)) ? (
          <Grid display={"flex"} className="add-dropdown-box">
            <Button
              className="add-dropdown-typo"
              id="add-input"
              onMouseDown={(e) => e.preventDefault()}
              onClick={(event) => addInput(event)}
              disableRipple
              startIcon={<TextFieldsIcon className="add-dropdown-circle" />}
            >
              Add input
            </Button>
            <>
              <Button
                className="add-dropdown-typo"
                id="add-dropdown"
                onMouseDown={preserveEditorSelectionForList}
                onClick={(e) => handleButtonClicks(e, "global_lists")}
                disableRipple
                startIcon={<MenuBookIcon className="add-dropdown-circle" />}
              >
                Global lists
              </Button>
              <Button
                className="add-dropdown-typo"
                id="add-dropdown"
                onMouseDown={preserveEditorSelectionForList}
                onClick={(e) => handleButtonClicks(e, "new_list")}
                disableRipple
                startIcon={<AddCircleOutlineOutlinedIcon className="add-dropdown-circle" />}
              >
                New list
              </Button>
            </>
          </Grid>
        ) : null}
      </div>
      <EditOptionsDialog handleDeleteDropdown={handleDeleteDropdown}
        dialogRef={dialogRef} id={id} state={state} setState={setState} handleSave={handleSave}
        filterTagList={filterTagList} addDropdown={addDropdown} categoryTags={categoryTags} newOpen={newOpen}
        canSave={dialogOptionList?.some((option) => option && option.trim().length > 0) && (!saveToGlobalLists || (saveToGlobalLists && name && name?.trim()?.length > 0))}
      />
      {openRemoveDropdown && <CopySummaryModal
        dialogRef={dialogRef}
        open={openRemoveDropdown}
        handleClose={handleCloseRemoveDropdown}
        handleSubmit={handleSubmitRemoveDropdown}
        submitButtonTitle={"Paste Without Dropdown"}
        dialogTitle={"Dropdown Won't Be Included"}
        dialogContentText={"Dropdowns cannot be copied and pasted. If you continue, only the text will be pasted without the dropdown."}
      />}
    </div >
  );
};

export default FroalaEditorComponent;
